// ─────────────────────────────────────────────────────────────────────────────
// REQ-096 — Cliente HTTP tipado del módulo de Reconocimiento.
//
// Aditivo: NO reemplaza `src/api/client.ts`. Apunta a su propio microservicio vía
// `import.meta.env.VITE_API_RECOGNITION_URL` y aplica los mismos interceptores que el cliente
// existente: header `X-Correlation-Id` (correlación) + `Authorization: Bearer <token>` (auth),
// más `X-Dev-Role` en desarrollo (lo lee DevAuthMiddleware).
// ─────────────────────────────────────────────────────────────────────────────

import { acquireTokenSilentSafe } from '@/auth/acquireToken';
import {
  getFrontlineToken,
  hasFrontlineSession,
  isFrontlineTokenExpired,
  forceFrontlineRelogin,
} from '@/auth/frontlineSession';
import { toNaturalName } from '@/utils/strings';
import { PERMISSION_ORDER } from './types';
import type {
  EuropeUsaImportResultDto,
  SyncSettingsDto,
  SaveSyncSettingsBody,
} from '@/api/types';
import type {
  RecognitionDto,
  RecognitionDetailDto,
  CampaignDto,
  PagedResultDto,
  CreateRecognitionBody,
  RecognitionFeedParams,
  ReportRecognitionBody,
  UpdateRecognitionBody,
  PromoteRecognitionBody,
  ReactionSummaryDto,
  ReactionUsersDto,
  ToggleReactionBody,
  StreakDto,
  PersonSummary,
  ManagerStatusDto,
  UserSearchResponse,
  PeopleSearchParams,
  RecognitionErrorBody,
  CampaignDocumentDto,
  WrappedDto,
  RecognitionBulkImportRowDto,
  RecognitionBulkImportResultDto,
  NotificationInboxResponse,
  PreferenceScope,
  NotificationRowDto,
  SystemBroadcastRequest,
  SystemBroadcastResult,
  RecognitionPreferenceDto,
  SaveRecognitionPreferenceRequest,
  ResolvedPreferencesDto,
  NotificationSelfPreferenceDto,
  TeamMetricsDto,
  RecognitionTeamParams,
  RecognitionKpiDto,
  RecognitionSummaryParams,
  AdminUserDto,
  UpdateUserRolesBody,
  PermissionMap,
  BusinessRuleDto,
  PointsConfigDto,
  PointsConfigSummaryDto,
  PointsSegment,
  PointsToggleDto,
  PointsExportPageDto,
  FrontlineCatalogs,
  FrontlineRegisterBody,
  FrontlineRegisterResult,
  FrontlineActivateBody,
  FrontlineLoginBody,
  FrontlineLoginResult,
  RegistrationOu,
  VerifyEmailResult,
  UpdateAvatarBody,
  FrontlineAvatar,
  UserProfile,
  LegalDocumentDto,
  LegalDocType,
  CorporateDocumentDto,
  CorporateDocumentForMeDto,
  UploadCorporateDocumentInput,
  UpdateCorporateDocumentBody,
  LegalAcceptanceStatus,
  LegalAcceptanceBody,
  LegalDocumentDraftDto,
  SaveLegalDocumentDraftBody,
  PublishLegalDocumentBody,
  DailyReportDto,
  CampaignReportDto,
  HeadcountSummaryDto,
  HistoryReportDto,
  CampaignSummaryDto,
  CampaignsGlobalSummaryDto,
  CampaignLiveKpiDto,
  HeatmapCellDto,
  OnaGraphDto,
  LeaderMetricRowDto,
  LeadersListResponseDto,
  WinnerDto,
  WinnersPageDto,
  WinnerSearchParams,
  NominationsRawExportPageDto,
  WinnersExportFormat,
  RecognitionReportParams,
  DocumentationPillDto,
  GenerationalFilters,
  DailyFunnelFilter,
  GenerationalReportDto,
  GenerationalDistributionReportDto,
  UpsertPillBody,
  ReportDto,
  ReportDetailDto,
  ModerationReportDto,
  BlockedWordDto,
  BlockedWordBody,
  CountryLanguageMapDto,
  ModerationNotifyPreviewDto,
  ModerationSettingsDto,
  UserViewSettingsDto,
  RegisterWinnerBody,
  ImportWinnersBody,
  WinnerImportResultDto,
  BulkImportUsersResultDto,
  CampaignStatus,
  CampaignApprovalDto,
  CampaignApprovalAction,
  CreateCampaignBody,
  UpdateCampaignBody,
  UploadCampaignDocumentInput,
  CampaignExceptionDto,
  ActiveCampaignExceptionDto,
  CampaignType,
  CreateCampaignExceptionBody,
  UpdateCampaignExceptionBody,
  ToggleCampaignExceptionBody,
  CampaignExceptionApprovalAction,
  CampaignExceptionListParams,
  CampaignExceptionDocumentDto,
  UploadCampaignExceptionDocumentInput,
  GestionSettingsDto,
  RecognitionRulesDto,
  RecognitionProgramSettingsDto,
  LandingBannerDto,
  AdminBadgeDto,
  StreakSettingsBody,
  RecapEditionDto,
  RecapEditionSummaryDto,
  WrappedAvailabilityDto,
} from './types';

/** Base URL del RecognitionService — configurable por entorno. */
export const RECOGNITION_BASE_URL: string = import.meta.env.VITE_API_RECOGNITION_URL ?? '';

/**
 * Base URL del UserAdminService — la búsqueda de personas (receptores / "a nombre de") vive ahí
 * (GET v1/users?search=). Reutiliza la MISMA variable que el cliente base (VITE_API_USERS_URL) para
 * no duplicar configuración.
 */
export const USERS_BASE_URL: string = import.meta.env.VITE_API_USERS_URL ?? '';

/**
 * Base URL del AnalyticsService — el Wrapped (resumen anual de reconocimiento) vive ahí
 * (GET v1/analytics/recognition/wrapped). Reutiliza la MISMA variable que el resto de analytics.
 */
export const ANALYTICS_BASE_URL: string = import.meta.env.VITE_API_ANALYTICS_URL ?? '';

/**
 * Base URL del RulesEngine — la config de puntos (GET/PUT v1/rules/points-config/{ouId}) vive ahí.
 * Reutiliza la MISMA variable que el resto del motor de reglas.
 */
export const RULES_BASE_URL: string = import.meta.env.VITE_API_RULES_URL ?? '';

/**
 * Base URL del NotificationService — la bandeja in-app (REQ-108) vive ahí. El endpoint de inbox
 * es D5 (REQ-071): hoy la campana queda tras el feature flag y con estado vacío honesto.
 */
export const NOTIFICATIONS_BASE_URL: string = import.meta.env.VITE_API_NOTIFICATIONS_URL ?? '';

/** Error tipado de la API de Reconocimiento (mismo contrato que ApiError del cliente base). */
export class RecognitionApiError extends Error {
  constructor(public status: number, public body: unknown, public correlationId?: string) {
    super(`Recognition API error ${status}`);
    this.name = 'RecognitionApiError';
  }
}

/**
 * Status sintético para un `fetch` que nunca llegó a tener respuesta HTTP (el navegador o un proxy
 * intermedio cortó la espera antes). Bug real, carga masiva del padrón Europa/EE. UU. (2026-09-04):
 * un archivo grande deja al backend procesando varios minutos (medido en local: 51s con Mongo y
 * Service Bus sin latencia real) y el cliente se rinde antes de que el backend responda — pero el
 * backend YA terminó de escribir todo con éxito. Antes, este `throw` crudo del `fetch` (un
 * `TypeError`, no un `RecognitionApiError`) ni siquiera se capturaba: se perdía sin correlationId y
 * `getRecognitionErrorMessage` caía al mismo fallback genérico que un error de configuración, sin
 * forma de distinguir "nunca hubo respuesta" de "el servidor sí respondió con un error".
 * Distinto de `0` (config ausente, ver `SIN_CONFIGURAR`): ese caso nunca genera correlationId porque
 * falla ANTES del `fetch`.
 */
export const NETWORK_ERROR_STATUS = -1;

/**
 * Resuelve el token de autorización. Si existe una sesión Frontline se usa SU token (nunca se mezcla
 * con Azure AD); si ese token ya expiró se fuerza re-login en vez de caer a MSAL (que fallaría porque
 * el Frontline no existe en Entra ID). Sin sesión Frontline → token corporativo de Azure AD/Teams.
 */
async function resolveAuthToken(): Promise<string> {
  const frontlineToken = getFrontlineToken();
  if (frontlineToken) {
    if (isFrontlineTokenExpired()) {
      forceFrontlineRelogin();
      throw new RecognitionApiError(401, { message: 'Sesión Frontline expirada' });
    }
    return frontlineToken;
  }
  return acquireTokenSilentSafe();
}

/**
 * Interruptor del mock in-repo del RecognitionService (D-8 — bloqueo B-1: no hay autoridad sobre
 * AKS para desplegar el servicio). Es EXPLÍCITO a propósito: sin esta variable, una URL ausente
 * sigue produciendo el error de configuración real, nunca datos inventados. Ver `./mock/index.ts`.
 *
 * El servicio real ya vive re-alojado dentro de SurveyService y está desplegado en QA/PROD
 * (`VITE_RECOGNITION_MOCK: 'false'` ahí desde la Sesión 2 de `ReemplazoRecognitionService`). El
 * mock se conserva SOLO para desarrollo local (D-R4, Sesión 4): esta máquina no tiene Docker/Mongo
 * levantado, así que sin él la totalidad de `/app/*` en local quedaría en error permanente.
 * Condición de retiro físico definitivo: cuando exista un backend local ejecutable.
 */
const RECOGNITION_MOCK_ENABLED: boolean = import.meta.env.VITE_RECOGNITION_MOCK === 'true';

type MockModule = typeof import('./mock');
let mockModule: Promise<MockModule> | null = null;

/**
 * Carga perezosa del mock. Con el interruptor apagado el `import()` no se ejecuta nunca, así que
 * el módulo —y su semilla— quedan fuera del bundle que se sirve a producción.
 */
function loadMock(): Promise<MockModule> {
  mockModule ??= import('./mock');
  return mockModule;
}

/**
 * Da al mock la primera oportunidad de atender la petición. Devuelve `null` cuando la ruta
 * pertenece a un servicio realmente desplegado (UserAdmin, Notification, RulesEngine): en ese
 * caso el llamador sigue con `fetch` contra el servicio de verdad.
 */
async function tryMock<T>(
  path: string,
  // Subconjunto estructural de `RequestInit`: es lo único que el enrutador del mock lee.
  options: { method?: string; body?: unknown },
): Promise<{ value: T } | null> {
  if (!RECOGNITION_MOCK_ENABLED) return null;
  const mock = await loadMock();
  const result = mock.handleMockRequest(path, options);
  if (result === mock.MOCK_MISS) return null;
  if (!result.ok) throw new RecognitionApiError(result.status, result.body);
  return { value: result.data as T };
}

function getDevRole(): string {
  try {
    const stored = localStorage.getItem('sigma-demo-user');
    if (!stored) return 'Employee';
    const parsed = JSON.parse(stored) as { role?: string };
    return parsed.role ?? 'Employee';
  } catch {
    return 'Employee';
  }
}

/**
 * Ejecuta una petición HTTP con los interceptores estándar del módulo.
 * Lanza `RecognitionApiError` en respuestas no-2xx para que TanStack Query las capture
 * como estado de error real. `baseUrl` permite reusar el mismo helper contra el
 * RecognitionService (default) o el UserAdminService (búsqueda de personas).
 */
/** Mensaje único de "config ausente": los cuatro helpers de red fallan igual y con el mismo texto. */
const SIN_CONFIGURAR = 'La URL del servicio no está configurada';

async function request<T>(
  path: string,
  options: RequestInit = {},
  baseUrl: string = RECOGNITION_BASE_URL,
): Promise<T> {
  const mocked = await tryMock<T>(path, options);
  if (mocked) return mocked.value;

  if (!baseUrl) {
    // Config ausente: fallar explícito (estado de error honesto en la UI), nunca datos falsos.
    throw new RecognitionApiError(0, {
      message: SIN_CONFIGURAR,
    });
  }

  // Sesión Frontline (JWT propio) tiene prioridad; si no hay, cae al token de Azure AD/Teams.
  // resolveAuthToken además fuerza re-login si el token Frontline expiró (no cae a MSAL).
  const token = await resolveAuthToken();
  const correlationId = crypto.randomUUID();

  let res: Response;
  try {
    res = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Correlation-Id': correlationId,
        ...(import.meta.env.DEV ? { 'X-Dev-Role': getDevRole() } : {}),
        ...(options.headers ?? {}),
      },
    });
  } catch (error) {
    // El `fetch` nunca llegó a tener respuesta (el cliente/proxy cortó la espera, o cayó la red).
    // El backend puede seguir corriendo y terminar con éxito — ver NETWORK_ERROR_STATUS. Se
    // preserva el mensaje crudo del navegador en el body por si ayuda a diagnosticar (nunca se
    // muestra tal cual al usuario, solo queda disponible para consola/soporte).
    const rawMessage = error instanceof Error ? error.message : String(error);
    throw new RecognitionApiError(NETWORK_ERROR_STATUS, { message: rawMessage }, correlationId);
  }

  if (!res.ok) {
    // 401 con sesión Frontline viva → token rechazado por el server (inválido/revocado): limpiar y re-login.
    if (res.status === 401 && hasFrontlineSession()) forceFrontlineRelogin();
    const body: unknown = await res.json().catch(() => null);
    throw new RecognitionApiError(res.status, body, correlationId);
  }

  // 204 No Content — p.ej. GET /campaigns/active cuando aplica el default de Fundamentales.
  if (res.status === 204) return undefined as unknown as T;

  try {
    return (await res.json()) as T;
  } catch (error) {
    // C7 — 200 OK CON CUERPO VACÍO (ej. `AuditDemograficoExport` → `return Ok();`, sin
    // Content-Length): `res.json()` sobre una cadena vacía lanza "Unexpected end of JSON input" y
    // convertía una auditoría que SÍ se registró en el servidor en un error fantasma en el cliente.
    // Se degrada a `undefined` en vez de propagar — el único costo es no detectar un JSON realmente
    // malformado en una respuesta 2xx, un caso patológico que hoy tampoco se distingue de este.
    if (import.meta.env.DEV) console.error('Respuesta 2xx sin JSON válido:', error);
    return undefined as unknown as T;
  }
}

/**
 * Variante de `request` para respuestas de texto plano (p.ej. la plantilla CSV de ganadores que el
 * WinnersController devuelve como text/csv). Aplica los mismos interceptores; lanza el mismo error tipado.
 */
async function requestText(
  path: string,
  options: RequestInit = {},
  baseUrl: string = RECOGNITION_BASE_URL,
): Promise<string> {
  if (RECOGNITION_MOCK_ENABLED) {
    const mock = await loadMock();
    const result = mock.handleMockRequestText(path, options);
    if (result !== mock.MOCK_MISS) {
      if (!result.ok) throw new RecognitionApiError(result.status, result.body);
      return result.data as string;
    }
  }

  if (!baseUrl) {
    throw new RecognitionApiError(0, { message: SIN_CONFIGURAR });
  }
  // Sesión Frontline (JWT propio) tiene prioridad; si no hay, cae al token de Azure AD/Teams.
  // resolveAuthToken además fuerza re-login si el token Frontline expiró (no cae a MSAL).
  const token = await resolveAuthToken();
  const res = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Correlation-Id': crypto.randomUUID(),
      ...(import.meta.env.DEV ? { 'X-Dev-Role': getDevRole() } : {}),
      ...(options.headers ?? {}),
    },
  });
  if (!res.ok) {
    // 401 con sesión Frontline viva → token rechazado por el server (inválido/revocado): limpiar y re-login.
    if (res.status === 401 && hasFrontlineSession()) forceFrontlineRelogin();
    const body: unknown = await res.json().catch(() => null);
    throw new RecognitionApiError(res.status, body);
  }
  return res.text();
}

/**
 * RF-079 — variante MULTIPART de `request` para subir un archivo con campos/arrays adicionales
 * (el `request` normal siempre es JSON). Aplica el mismo interceptor de token; NO fija Content-Type
 * (el navegador pone el boundary de multipart/form-data). Devuelve el JSON de respuesta tipado.
 */
async function requestUpload<T>(
  path: string,
  form: FormData,
  baseUrl: string = USERS_BASE_URL,
): Promise<T> {
  if (!baseUrl) {
    throw new RecognitionApiError(0, { message: SIN_CONFIGURAR });
  }
  const token = await resolveAuthToken();
  const res = await fetch(`${baseUrl}${path}`, {
    method: 'POST',
    body: form,
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Correlation-Id': crypto.randomUUID(),
      ...(import.meta.env.DEV ? { 'X-Dev-Role': getDevRole() } : {}),
    },
  });
  if (!res.ok) {
    if (res.status === 401 && hasFrontlineSession()) forceFrontlineRelogin();
    const body: unknown = await res.json().catch(() => null);
    throw new RecognitionApiError(res.status, body);
  }
  return (await res.json()) as T;
}

/**
 * Variante de `request` para respuestas BINARIAS (la plantilla `.xlsx` de Ganadores).
 *
 * No vale `requestText`: decodificar un ZIP como UTF-8 lo corrompe, y el fichero descargado se abre
 * roto. Mismo interceptor de token y mismo error tipado.
 */
async function requestBlob(
  path: string,
  baseUrl: string = RECOGNITION_BASE_URL,
): Promise<Blob> {
  // Modo mock: el handler de texto sigue sirviendo la plantilla, envuelta en un Blob. Sin esto, la
  // descarga sería lo único de la pantalla que intentara salir a la red con el mock encendido.
  if (RECOGNITION_MOCK_ENABLED) {
    const mock = await loadMock();
    const result = mock.handleMockRequestText(path, {});
    if (result !== mock.MOCK_MISS) {
      if (!result.ok) throw new RecognitionApiError(result.status, result.body);
      return new Blob([result.data as string], { type: 'text/csv;charset=utf-8;' });
    }
  }

  if (!baseUrl) {
    throw new RecognitionApiError(0, { message: SIN_CONFIGURAR });
  }
  const token = await resolveAuthToken();
  const res = await fetch(`${baseUrl}${path}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Correlation-Id': crypto.randomUUID(),
      ...(import.meta.env.DEV ? { 'X-Dev-Role': getDevRole() } : {}),
    },
  });
  if (!res.ok) {
    if (res.status === 401 && hasFrontlineSession()) forceFrontlineRelogin();
    const body: unknown = await res.json().catch(() => null);
    throw new RecognitionApiError(res.status, body);
  }
  return res.blob();
}

function buildQuery(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * P3-4 — Nombres naturales dentro de un reconocimiento.
 *
 * `emisorNombre` y `receptorNombres` viajan DENTRO del feed y no pasan por `toPersonSummary`, así
 * que sin esto los post-its del muro seguirían firmando "(Ext.) De la Cruz Morales, Ernesto"
 * aunque la tarjeta de perfil ya mostrara el nombre natural. Misma regla, aplicada en la entrada.
 */
function withNaturalNames(r: RecognitionDto): RecognitionDto {
  return {
    ...r,
    emisorNombre: r.emisorNombre ? toNaturalName(r.emisorNombre) : r.emisorNombre,
    receptorNombres: r.receptorNombres.map((n) => toNaturalName(n)),
  };
}

export const recognitionApi = {
  /** GET v1/recognitions — feed paginado de reconocimientos (SIN puntos). */
  getRecognitions: (params: RecognitionFeedParams = {}) =>
    request<PagedResultDto<RecognitionDto>>(
      `/v1/recognitions${buildQuery({
        ou: params.ou,
        campaignId: params.campaignId,
        receptorId: params.receptorId,
        emisorId: params.emisorId,
        programa: params.programa,
        page: params.page,
        pageSize: params.pageSize,
        desde: params.desde,
        hasta: params.hasta,
        soloVigentes: params.soloVigentes ? 'true' : undefined,
      })}`,
    ).then((res) => ({ ...res, items: res.items.map(withNaturalNames) })),

  /** GET v1/recognitions/{id} — detalle con snapshots. */
  getRecognitionById: (id: string) =>
    request<RecognitionDetailDto>(`/v1/recognitions/${encodeURIComponent(id)}`).then((res) => ({
      ...res,
      recognition: withNaturalNames(res.recognition),
      // T4 — emisorSnapshot llega null cuando el reconocimiento es anónimo y quien mira no es admin.
      emisorSnapshot: res.emisorSnapshot
        ? { ...res.emisorSnapshot, displayName: toNaturalName(res.emisorSnapshot.displayName) }
        : null,
      receptorSnapshots: res.receptorSnapshots.map((s) => ({ ...s, displayName: toNaturalName(s.displayName) })),
    })),

  /** POST v1/recognitions — crea un reconocimiento. */
  createRecognition: (body: CreateRecognitionBody) =>
    request<RecognitionDto>('/v1/recognitions', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** PATCH v1/recognitions/{id} — edita un reconocimiento (solo si la config lo habilita, REQ-107). */
  updateRecognition: (id: string, body: UpdateRecognitionBody) =>
    request<RecognitionDto>(`/v1/recognitions/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /** DELETE v1/recognitions/{id} — elimina un reconocimiento propio (con confirmación en la UI). */
  deleteRecognition: (id: string) =>
    request<{ success: boolean }>(`/v1/recognitions/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  /** GET v1/campaigns/active?ou= — campaña activa para una OU. 204 → default Fundamentales (null). */
  getActiveCampaign: (ou: string) =>
    request<CampaignDto | undefined>(`/v1/campaigns/active${buildQuery({ ou })}`),

  /** GET v1/campaigns/next-brillamos?ou= — próxima campaña Brillamos programada (T5). 204 → null. */
  getNextBrillamos: (ou: string) =>
    request<CampaignDto | undefined>(`/v1/campaigns/next-brillamos${buildQuery({ ou })}`),

  /** GET v1/campaigns/{id}/documents — documentos de la campaña (instructivos/legales) por idioma. */
  getCampaignDocuments: (campaignId: string) =>
    request<CampaignDocumentDto[]>(`/v1/campaigns/${encodeURIComponent(campaignId)}/documents`),

  /**
   * GET v1/analytics/recognition/wrapped — resumen anual del usuario. Vive en el AnalyticsService,
   * por eso apunta a ANALYTICS_BASE_URL. SIN puntos (regla de oro).
   *
   * RF-130 — `year` es el AÑO EFECTIVO que informa `GET v1/recap/availability`, no el año en curso
   * calculado en el cliente. El parámetro ya viajaba antes de RF-130, pero el controlador ni siquiera
   * lo declaraba y se descartaba en silencio; S2 lo hace vinculante.
   *
   * RF-130 corrección F3 (H-07, mitad cliente) — `fromMonth` es el `desdeMes` de esa misma
   * disponibilidad (mes de ingreso del titular, sólo si ingresó a mitad del año). `null`/`undefined`
   * (la inmensa mayoría) no viaja: el servidor lee su ausencia como "año completo", la misma ruta de
   * siempre (G4, `GetRecognitionWrappedQueryHandler`).
   */
  getMyWrapped: (year?: number, fromMonth?: number | null) =>
    request<WrappedDto>(
      `/v1/analytics/recognition/wrapped${buildQuery({ year, fromMonth })}`,
      {},
      ANALYTICS_BASE_URL,
    ),

  /** POST v1/recognitions/{id}/report — reporta un reconocimiento (nunca anónimo; oculta al revisar). */
  reportRecognition: (id: string, body: ReportRecognitionBody) =>
    request<{ success: boolean }>(`/v1/recognitions/${encodeURIComponent(id)}/report`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** POST v1/recognitions/{id}/promote — promueve un reconocimiento diario a una campaña Brillamos. */
  promoteRecognition: (id: string, body: PromoteRecognitionBody) =>
    request<RecognitionDto>(`/v1/recognitions/${encodeURIComponent(id)}/promote`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * GET v1/recognitions/{id}/reactions — resumen de reacciones. Backend real (ver
   * docs/superpowers/specs/2026-08-06-reacciones-postit-design.md); mismo contrato disponible
   * simulado en `mock/` para desarrollo local (`VITE_RECOGNITION_MOCK=true`).
   */
  getReactions: (recognitionId: string) =>
    request<ReactionSummaryDto>(`/v1/recognitions/${encodeURIComponent(recognitionId)}/reactions`),

  /**
   * POST v1/recognitions/{id}/reactions — toggle de la reacción del usuario autenticado (agrega,
   * reemplaza o quita). Backend real; mismo contrato simulado en `mock/`.
   */
  toggleReaction: (recognitionId: string, body: ToggleReactionBody) =>
    request<ReactionSummaryDto>(`/v1/recognitions/${encodeURIComponent(recognitionId)}/reactions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * GET v1/recognitions/{id}/reactions/users — quiénes reaccionaron (con nombre resuelto). Se pide
   * bajo demanda al abrir la lista, no por cada tarjeta. Mismo contrato simulado en `mock/`.
   */
  getReactionUsers: (recognitionId: string) =>
    request<ReactionUsersDto>(`/v1/recognitions/${encodeURIComponent(recognitionId)}/reactions/users`),

  /** GET v1/streaks/me — racha del usuario actual (SIN puntos). */
  getMyStreak: (programa?: string | null) =>
    request<StreakDto>('/v1/streaks/me' + (programa ? `?programa=${encodeURIComponent(programa)}` : '')),

  /**
   * GET v1/users?search= — búsqueda de personas para el selector de receptores / "a nombre de".
   * Vive en el UserAdminService, por eso apunta a USERS_BASE_URL (no al RecognitionService).
   */
  /**
   * GET v1/users/search — selector de personas ("a nombre de", receptores).
   *
   * 🔴 NO es `GET /v1/users`. Los dos usos compartían ese endpoint, que **no filtra por
   * `IsActive` en ningún lado**: dar de baja a alguien NO lo sacaba del buscador, así que la
   * regla acordada en junta —«no se elimina, se cambia el estatus»— no se cumplía en la
   * práctica. Hoy no se nota porque hay 0 inactivos; en cuanto SuccessFactors empiece a dar
   * bajas, un empleado que ya no está seguiría apareciendo como destinatario válido.
   *
   * El endpoint dedicado filtra `IsActive = true` y **sí incluye las fichas sin reclamar**, que
   * es la gracia del padrón: se puede reconocer a quien todavía no ha entrado.
   *
   * Devuelve una LISTA, no una página: el tope es duro (máx. 50) para que el selector no pueda
   * paginar el directorio completo. Se envuelve aquí para no cambiar el contrato del hook.
   */
  searchPeople: async (params: PeopleSearchParams): Promise<UserSearchResponse> => {
    const items = await request<PersonSummary[]>(
      `/v1/users/search${buildQuery({
        q: params.search,
        ouId: params.ouId,
        limit: params.pageSize,
      })}`,
      {},
      USERS_BASE_URL,
    );

    return { items, totalCount: items.length, page: 1, pageSize: items.length };
  },

  /**
   * GET v1/recognitions/bulk/template — plantilla CSV real servida por el backend (columnas del
   * contrato congelado). Nunca construir la plantilla a mano en el cliente: evita que el front se
   * desincronice de las columnas reales, como pasaba antes (REQ-106).
   *
   * `campanaId`/`programa` son opcionales y le dicen al servidor qué Fundamentales están retirados
   * del PROGRAMA (Gestión, WP-F5) para esta carga: sin ellos, la plantilla ofrecía columna de marca
   * para un Fundamental activo en el catálogo general pero ya excluido de Brillamos/Fundamentales —
   * mismo criterio que `fundamentalesOfrecibles` ya aplica al elegir Fundamental fila por fila en el
   * paso de revisión.
   */
  bulkTemplate: (lang: string, campanaId?: string | null, programa?: string | null) =>
    requestText(`/v1/recognitions/bulk/template${buildQuery({
      lang,
      campanaId: campanaId ?? undefined,
      programa: programa ?? undefined,
    })}`),

  /**
   * POST v1/recognitions/bulk — carga masiva de reconocimientos por CSV (RN-CSV01..04 / REQ-085).
   * `preview:true` valida el lote completo sin escribir nada; `false` confirma la carga PARCIAL
   * (las filas válidas se cargan aunque algunas fallen). `campanaId` gobierna el permiso por
   * campaña (RN-CSV04); `null` = reconocimiento diario.
   *
   * `programa` dice DESDE QUÉ programa se carga, igual que en `getRecognitionRules`: sin él, un lote
   * sin campaña se validaba en el servidor contra Fundamentales (el doc global) mientras el botón se
   * había habilitado con el toggle de Brillamos — el wizard moría con un 403 en el primer preview.
   * Con campaña el servidor lo ignora: ahí el programa lo fija el Tipo de la campaña.
   */
  bulkUpload: (
    rows: RecognitionBulkImportRowDto[],
    campanaId: string | null,
    preview: boolean,
    programa?: string | null,
  ) =>
    request<RecognitionBulkImportResultDto>('/v1/recognitions/bulk', {
      method: 'POST',
      // `programa` se omite del cuerpo cuando no viene (JSON.stringify descarta `undefined`): el
      // backend lo trata como Fundamentales, que es el comportamiento previo.
      body: JSON.stringify({ rows, campanaId, preview, programa: programa ?? undefined }),
    }),

  /**
   * GET v1/recognitions/export-raw?from=&to= — página del export CRUDO de TODOS los reconocimientos
   * de un rango de fechas, paridad diaria de `winnersApi.exportNominationsRaw` (por campaña). Lo
   * consume el reporte "Reconocimiento Diario" para su hoja "Todos los reconocimientos" — ver
   * `fetchRecognitionsCrudoPaginado` en `../../fetchRecognitionsCrudas.ts`.
   */
  exportRaw: (from: string, to: string, page: number, pageSize: number) =>
    request<NominationsRawExportPageDto>(
      `/v1/recognitions/export-raw${buildQuery({ from, to, page, pageSize })}`),
};

// ─── AnalyticsService — Reportería de Reconocimiento (WP-08, v1/analytics/recognition/*) ─────────
//
// Todos los endpoints viven en el AnalyticsService (como el Wrapped), por eso apuntan a
// ANALYTICS_BASE_URL. Reutilizan el mismo `request`/`buildQuery` con interceptores. SIN puntos.

/**
 * Mapea los filtros compartidos del FilterBar (`ou`/`startDate`/`endDate`) a los nombres de
 * parámetro reales de `RecognitionAnalyticsController` (`scope`/`from`/`to`). El backend nunca
 * aceptó los nombres del front — ASP.NET los descartaba en el binding y los filtros de OU/fecha no
 * tenían ningún efecto.
 *
 * 🔴 `fundamentalId`/`personaId` YA VIAJAN (2026-09-03). Antes se omitían con la nota «ningún
 * endpoint de Reportería los acepta todavía» — cierto entonces, y la razón por la que los dos
 * selectores estaban a la vista sin efecto en cinco pestañas. Ahora los aceptan `daily`, `campaign`,
 * `generational`, `network` y `leaders-list`.
 *
 * `heatmap` los recibe y los ignora: su matriz es OU × Fundamental, así que el recorte por
 * Fundamental se hace en el cliente (`HeatmapTab`) y el de persona no aplica — la pantalla lo dice
 * con `AvisoFiltroNoAplica`.
 */
/**
 * Sección 7 — query del tab Brillamos Juntos. Sobre el rango añade la campaña y el Fundamental.
 *
 * `fundamentalId` es el «filtro de top 3 por Fundamental (requerimiento de Europa)». **Antes no se
 * enviaba**, y la barra de filtros superior —que este tab SÍ muestra— tenía el selector de Fundamental
 * a la vista sin ningún efecto: el mismo defecto que el calendario del Diario antes de T-05, un filtro
 * que se ve y no filtra.
 *
 * 🔴 No se mete en `toReportRangeQuery`: ese helper lo comparten varios endpoints y uno que no acepte
 * el parámetro lo recibiría igual.
 */
export function toCampaignQuery(p: RecognitionReportParams): {
  scope: string | undefined;
  scopeIds: string | undefined;
  from: string | undefined;
  to: string | undefined;
  campaignId: string | undefined;
  fundamentalId: string | undefined;
  personaId: string | undefined;
  modalidad: string | undefined;
  top: number | undefined;
} {
  return {
    // `fundamentalId`/`personaId` ya llegan normalizados (vacío ⇒ undefined) desde
    // `toReportRangeQuery`; no hace falta repetir la normalización aquí.
    ...toReportRangeQuery(p),
    campaignId: p.campaignId,
    // 🔴 El backend liga a un enum de C# (`RecibosModalidad`), así que viaja su NOMBRE, no el del
    // tipo del front. `todos` no se manda: es el valor por omisión, y omitirlo mantiene la URL —y la
    // clave de caché— idéntica a la de antes de existir este filtro.
    modalidad: p.modalidad && p.modalidad !== 'todos'
      ? (p.modalidad === 'personaUnica' ? 'SoloPersonaUnica' : 'SoloCrossOu')
      : undefined,
    // Tamaño del ranking elegido en pantalla. El backend lo clampea a 200.
    top: p.top,
  };
}

export function toReportRangeQuery(p: RecognitionReportParams): {
  scope: string | undefined;
  scopeIds: string | undefined;
  from: string | undefined;
  to: string | undefined;
  fundamentalId: string | undefined;
  personaId: string | undefined;
} {
  return {
    scope: p.ou,
    scopeIds: p.ouIds?.length ? p.ouIds.join(',') : undefined,
    from: p.startDate,
    to: p.endDate,
    // Vacío ⇒ undefined: `buildQuery` no debe mandar `fundamentalId=`/`personaId=`, que el backend
    // tendría que interpretar como «el Fundamental/la persona sin id».
    fundamentalId: p.fundamentalId || undefined,
    personaId: p.personaId || undefined,
  };
}

/**
 * Filtros que acepta la Reportería de Moderación (`v1/moderation/report`): OU, rango y persona, con
 * los mismos nombres de query string que AnalyticsService.
 *
 * 🔴 `fundamentalId` NO viaja, y esa ausencia implementa una regla: el reporte se arma sobre los
 * casos reportados, no sobre los Fundamentales del reconocimiento. Mandarlo para que el backend lo
 * ignorara sería justo el «filtro que no filtra» que la pantalla evita declarándolo con
 * `AvisoFiltroNoAplica`. Va en un helper propio y no en `toReportRangeQuery`, que lo comparten
 * endpoints que sí aceptan Fundamental.
 */
export function toModerationReportQuery(p: RecognitionReportParams): {
  scopeIds: string | undefined;
  from: string | undefined;
  to: string | undefined;
  personaId: string | undefined;
} {
  // `ou` (una sola OU) y `ouIds` (la selección ya expandida) se unen en un CSV sin repetidos: el
  // backend trocea por comas, igual que `MergeScopeIds` en AnalyticsService.
  const ouIds = [...new Set([...(p.ou ? [p.ou] : []), ...(p.ouIds ?? [])])];
  return {
    scopeIds: ouIds.length > 0 ? ouIds.join(',') : undefined,
    from: p.startDate,
    to: p.endDate,
    personaId: p.personaId || undefined,
  };
}

/**
 * T-05 — `GetDaily` YA acepta rango. Antes solo tomaba `reference` (un punto en el tiempo) y comparaba
 * la semana que lo contenía contra la previa: `startDate` se descartaba, así que el calendario del
 * panel estaba a la vista sin hacer nada. Eso producía el «sí hemos hecho reconocimientos en estas
 * fechas y me muestra todo en cero» del reporte de UAT.
 *
 * Ahora viajan `from`/`to` (los dos o ninguno: el backend responde 400 con uno solo) y `reference` se
 * mantiene como ancla del periodo POR OMISIÓN y del mes del embudo.
 *
 * E5 — `anio`/`mes` filtran el Embudo de Adopción de forma INDEPENDIENTE de `scope`/`reference`
 * (Tarea E2, backend): si no vienen, el backend usa el mes calendario que contiene `reference`.
 */
export function toDailyQuery(
  p: RecognitionReportParams,
  funnel: DailyFunnelFilter = {},
): {
  scope: string | undefined; scopeIds: string | undefined; reference: string | undefined;
  from: string | undefined; to: string | undefined;
  anio: number | undefined; mes: number | undefined;
  top: number | undefined;
  bu: string | undefined; depto: string | undefined; site: string | undefined;
  fundamentalId: string | undefined; personaId: string | undefined;
} {
  // Los dos extremos van juntos: con uno solo el backend responde 400, así que si el usuario solo
  // fijó un lado se manda el periodo por omisión (la semana de `reference`) en vez de una petición
  // que va a fallar.
  const rangoCompleto = Boolean(p.startDate && p.endDate);
  return {
    // Tamaño de los Top elegido en pantalla. El backend lo clampea a 200.
    top: p.top,
    scope: p.ou,
    scopeIds: p.ouIds?.length ? p.ouIds.join(',') : undefined,
    reference: p.endDate,
    from: rangoCompleto ? p.startDate : undefined,
    to: rangoCompleto ? p.endDate : undefined,
    anio: funnel.anio,
    mes: funnel.mes,
    // Tira de filtros de la maqueta. Vacío ⇒ `undefined`, para que `buildQuery` no mande `bu=` y el
    // backend no vea una cadena vacía que tendría que interpretar como «BU sin nombre».
    bu: p.bu || undefined,
    depto: p.depto || undefined,
    site: p.site || undefined,
    fundamentalId: p.fundamentalId || undefined,
    personaId: p.personaId || undefined,
  };
}

export const recognitionReportsApi = {
  /** GET v1/analytics/recognition/daily — comparativa semana vs previa, embudo y tops (REQ-077). */
  getDaily: (p: RecognitionReportParams = {}, funnel: DailyFunnelFilter = {}) =>
    request<DailyReportDto>(
      `/v1/analytics/recognition/daily${buildQuery(toDailyQuery(p, funnel))}`,
      {},
      ANALYTICS_BASE_URL,
    ),

  /**
   * POST v1/analytics/recognition/daily-export-audit: deja constancia en bitácora (RN-K03) de que
   * el export de 7 hojas del reporte "Reconocimiento Diario" (`DailyTab.tsx`) se descargó. El Excel
   * lo arma el FRONTEND client-side reutilizando los datos ya traídos de `getDaily` — el backend no
   * sirve ninguna descarga, solo audita. Mismo patrón que `auditDemograficoExport`/
   * `auditLeadersListExport`: fire-and-forget, nunca bloquea la descarga ya completada.
   */
  auditDailyExport: (scope: string | undefined, rowCount: number) =>
    request<void>(
      `/v1/analytics/recognition/daily-export-audit${buildQuery({ scope, rowCount })}`,
      { method: 'POST' },
      ANALYTICS_BASE_URL,
    ),

  /** GET v1/analytics/recognition/campaign — ranking de nominaciones de una campaña (REQ-078). */
  /**
   * Sección 7 — `fundamentalId` viaja para el «filtro de top 3 por Fundamental (requerimiento de
   * Europa)» del tab Brillamos Juntos.
   *
   * 🔴 No se añade a `toReportRangeQuery`, que es compartido: ese helper alimenta varios endpoints y
   * uno que no acepte el parámetro lo recibiría igual. Va explícito aquí.
   *
   * Antes NO se enviaba, y la barra de filtros superior —que el tab Brillamos SÍ muestra— tenía el
   * selector de Fundamental a la vista sin ningún efecto. Mismo defecto que el calendario del Diario
   * antes de T-05: un filtro que se ve y no filtra.
   */
  /**
   * Bloque de plantilla del armazón. Solo `scope`: el endpoint NO acepta rango de fechas porque el
   * censo es una foto del directorio, no una medición del periodo.
   */
  getHeadcount: (p: RecognitionReportParams = {}) =>
    request<HeadcountSummaryDto>(
      `/v1/analytics/recognition/headcount${buildQuery({
        scope: p.ou,
        scopeIds: p.ouIds?.length ? p.ouIds.join(',') : undefined,
      })}`,
      {},
      ANALYTICS_BASE_URL,
    ),

  getCampaign: (p: RecognitionReportParams = {}) =>
    request<CampaignReportDto>(
      `/v1/analytics/recognition/campaign${buildQuery(toCampaignQuery(p))}`,
      {},
      ANALYTICS_BASE_URL,
    ),

  /**
   * GET v1/analytics/recognition/history — RF-150: histórico completo (Nombre/Nominaciones) de
   * una campaña. Sin rango de fechas: el alcance temporal lo da la propia campaña, no el FilterBar
   * superior — solo se pasa `scope` (OU) y `campaignId`.
   */
  getHistory: (p: RecognitionReportParams = {}) =>
    request<HistoryReportDto>(
      `/v1/analytics/recognition/history${buildQuery({ scope: p.ou, campaignId: p.campaignId })}`,
      {},
      ANALYTICS_BASE_URL,
    ),

  /** GET v1/analytics/recognition/campaign-live — KPIs en vivo de una campaña activa (REQ-082). */
  getCampaignLive: (p: RecognitionReportParams = {}) =>
    request<CampaignLiveKpiDto>(
      `/v1/analytics/recognition/campaign-live${buildQuery({ ...toReportRangeQuery(p), campaignId: p.campaignId })}`,
      {},
      ANALYTICS_BASE_URL,
    ),

  /**
   * GET v1/analytics/recognition/campaign-summary — RF-151: Reconocimientos/Audiencia de una
   * campaña. `fundamentalId` acota el resumen al Fundamental elegido en la tira (2026-09-04, vuelta
   * atrás del punto 5 de la ola anterior): en «Brillamos Juntos» el ranking ya se filtraba por
   * Fundamental y estos KPIs no, así que quedaban leyendo números de universos distintos sobre la
   * misma pantalla. `Audiencia` es la excepción: el backend no la acota por Fundamental (es la
   * plantilla CONVOCADA por la campaña, no depende de quién reconoció a quién), de ahí que
   * `ShineTab.tsx` siga avisando solo sobre «Total de plantilla»
   * (`AvisoFiltroNoAplica motivo="audienciaCampana"`). Vacío ⇒ `undefined`, mismo criterio que el
   * resto de los endpoints de Reportería.
   */
  getCampaignSummary: (campaignId: string, scope?: string, fundamentalId?: string) =>
    request<CampaignSummaryDto>(
      `/v1/analytics/recognition/campaign-summary${buildQuery({
        scope, campaignId, fundamentalId: fundamentalId || undefined,
      })}`,
      {},
      ANALYTICS_BASE_URL,
    ),

  /**
   * GET v1/analytics/recognition/campaign-export — Tarea D5/D6: mismo shape que `campaign-summary`
   * (`CampaignSummaryDto` — el propio contrato del backend lo confirma), pero exclusivo del botón
   * «Descargar Excel» de `CampaignRowActions`: cada 200 exitoso queda auditado server-side dentro
   * del propio handler (`IRecognitionExportAuditRepository`). No reemplaza a `getCampaignSummary` en
   * las tablas — llamarlo fuera de una descarga real generaría auditorías fantasma.
   */
  getCampaignExport: (campaignId: string, scope?: string) =>
    request<CampaignSummaryDto>(
      `/v1/analytics/recognition/campaign-export${buildQuery({ scope, campaignId })}`,
      {},
      ANALYTICS_BASE_URL,
    ),

  /**
   * GET v1/analytics/recognition/campaigns-export-global — P18-ter: reporte GLOBAL de campañas
   * (todas, no una), calcado de `03 - Historicos.xlsx`. Auditado server-side (Kind
   * "campaigns-export-global"), igual criterio que `getCampaignExport`.
   */
  getCampaignsGlobalExport: (scope?: string) =>
    request<CampaignsGlobalSummaryDto>(
      `/v1/analytics/recognition/campaigns-export-global${buildQuery({ scope })}`,
      {},
      ANALYTICS_BASE_URL,
    ),

  /** GET v1/analytics/recognition/heatmap — celdas OU × Fundamental con score de madurez (REQ-081). */
  getHeatmap: (p: RecognitionReportParams = {}) =>
    request<HeatmapCellDto[]>(
      `/v1/analytics/recognition/heatmap${buildQuery(toReportRangeQuery(p))}`,
      {},
      ANALYTICS_BASE_URL,
    ),

  /** GET v1/analytics/recognition/network — grafo ONA cross-OU (REQ-081). */
  getNetwork: (p: RecognitionReportParams = {}) =>
    request<OnaGraphDto>(
      `/v1/analytics/recognition/network${buildQuery(toReportRangeQuery(p))}`,
      {},
      ANALYTICS_BASE_URL,
    ),

  /**
   * GET v1/analytics/recognition/generational — Tab Demográfico: distribución por banda
   * generacional. `nombre` ya viene como el nombre de la banda (ej. "Millennials"), no un id —
   * GetRecognitionGenerationalQueryHandler no tiene el bug de nombres crudos de los demás.
   * RF-152 — `filters` son los propios del tab (BU/Depto/Site/Líderes-Pares), independientes del
   * FilterBar compartido.
   *
   * C5/C7 (2026-08-13, BREAKING) — antes devolvía un array plano de `RankedEntryDto`; ahora envuelve
   * la distribución (`bandas`) junto con `ousConCobertura` (ids de OU con cobertura de fecha de
   * nacimiento suficiente para aportar al cálculo). Ver `GenerationalReportDto`.
   */
  getGenerational: (p: RecognitionReportParams = {}, filters: GenerationalFilters = {}) =>
    request<GenerationalReportDto>(
      `/v1/analytics/recognition/generational${buildQuery({
        ...toReportRangeQuery(p),
        bu: filters.bu,
        depto: filters.depto,
        site: filters.site,
        lideresOPares: filters.lideresOPares,
      })}`,
      {},
      ANALYTICS_BASE_URL,
    ),

  // Censo de plantilla, no actividad de periodo: sin from/to a propósito (ver
  // GetRecognitionGenerationalDistributionQuery, backend). `scopeIds` sigue el mismo mecanismo
  // multi-OU que `getHeadcount`/`getHeatmap`/`getNetwork`/`getGenerational` (vía p.ouIds) — antes
  // solo mandaba `scope: p.ou`, que RecognitionAnalyticsView.tsx ya no puebla, dejando esta hoja
  // del export sin el filtro de OU que sí aplican sus hermanas.
  getGenerationalDistribution: (p: RecognitionReportParams = {}) =>
    request<GenerationalDistributionReportDto>(
      `/v1/analytics/recognition/generational-distribution${buildQuery({
        scope: p.ou,
        scopeIds: p.ouIds?.length ? p.ouIds.join(',') : undefined,
      })}`,
      {},
      ANALYTICS_BASE_URL,
    ),

  /**
   * GET v1/analytics/recognition/leaders — Tab Líderes: métricas por OU para OU Ambassadors
   * (REQ-088, fase 1 = tabla + export Excel). `ouName`/`embajador` NO vienen resueltos (el id
   * crudo) — el tab los resuelve con useOuNameResolver/usePersonNameResolver, igual que Heatmap.
   */
  getLeaders: (p: RecognitionReportParams = {}) =>
    request<LeaderMetricRowDto[]>(
      `/v1/analytics/recognition/leaders${buildQuery(toReportRangeQuery(p))}`,
      {},
      ANALYTICS_BASE_URL,
    ),

  /**
   * GET v1/analytics/recognition/leaders-list — Fase F: listado agregado de TODOS los líderes del
   * scope (persona, no OU), con tamaño de equipo cascadeado (directos+indirectos) y reconocimientos
   * por líder y por equipo. `liderNombre`/`ouNombre` YA VIENEN RESUELTOS por el backend — a
   * diferencia de `getLeaders` (OU-based), no hace falta resolverlos en el front.
   */
  getLeadersList: (p: RecognitionReportParams = {}) =>
    request<LeadersListResponseDto>(
      `/v1/analytics/recognition/leaders-list${buildQuery(toReportRangeQuery(p))}`,
      {},
      ANALYTICS_BASE_URL,
    ),

  /**
   * Fase F — POST v1/analytics/recognition/leaders-list-export-audit: deja constancia en bitácora
   * (RN-K03) de que el export "06 - Lideres.xlsx" se descargó. El Excel lo arma el FRONTEND
   * client-side reutilizando los datos ya traídos de `getLeadersList` — el backend no sirve ninguna
   * descarga, solo audita. 200 OK sin cuerpo de respuesta.
   *
   * El llamador debe tratarla como fire-and-forget (mismo patrón que `auditDemograficoExport`): si
   * falla, se loguea (guard DEV) pero NUNCA debe bloquear la descarga del Excel, que ya se completó
   * client-side antes de llamar aquí.
   */
  auditLeadersListExport: (scope: string | undefined, rowCount: number) =>
    request<void>(
      `/v1/analytics/recognition/leaders-list-export-audit${buildQuery({ scope, rowCount })}`,
      { method: 'POST' },
      ANALYTICS_BASE_URL,
    ),

  /**
   * GET v1/analytics/recognition/winners-export?format=raw|cascaded — ganadores de una campaña por
   * cascadeo, NUNCA top global (CA-046 / WP-08). Alimenta el export Excel del front. Este endpoint
   * no acepta rango de fechas (ver `RecognitionAnalyticsController.GetWinnersExport`).
   */
  getWinnersExport: (format: WinnersExportFormat, p: RecognitionReportParams = {}) =>
    request<WinnerDto[]>(
      `/v1/analytics/recognition/winners-export${buildQuery({
        format, campaignId: p.campaignId, scope: p.ou,
        scopeIds: p.ouIds?.length ? p.ouIds.join(',') : undefined,
      })}`,
      {},
      ANALYTICS_BASE_URL,
    ),

  /**
   * REQ-124 — GET v1/analytics/recognition/team — métricas del equipo del líder. `managerId` solo lo
   * respeta el backend si el caller es global (un líder siempre ve su propio equipo). SIN puntos.
   */
  getTeam: (p: RecognitionTeamParams = {}) =>
    request<TeamMetricsDto>(
      `/v1/analytics/recognition/team${buildQuery({ managerId: p.managerId, from: p.from, to: p.to })}`,
      {},
      ANALYTICS_BASE_URL,
    ),

  /**
   * REQ-125 — GET v1/analytics/recognition/summary — tablero de KPIs K-01..K-10 (+K-11/K-12 con
   * includeInactivity). `scope` acota la OU; sin scope el backend usa el alcance del rol. SIN puntos.
   */
  getSummary: (p: RecognitionSummaryParams = {}) =>
    request<RecognitionKpiDto[]>(
      `/v1/analytics/recognition/summary${buildQuery({
        scope: p.scope, from: p.from, to: p.to,
        includeInactivity: p.includeInactivity ? 'true' : undefined,
        // RF-143 — con cualquiera de los dos el backend calcula desde los recibos de proyección en
        // vez del agregado; sin ellos la petición es exactamente la de siempre.
        fundamentalId: p.fundamentalId, generacion: p.generacion,
      })}`,
      {},
      ANALYTICS_BASE_URL,
    ),

  /**
   * C7 (2026-08-13) — POST v1/analytics/recognition/demografico-export-audit: deja constancia en
   * bitácora (RN-K03) de que el export consolidado del panel Demográfico (Heatmap + Red cross-OU +
   * Generacional, 3 hojas en un solo Excel) se descargó. El Excel lo arma el FRONTEND client-side
   * reutilizando los datos ya traídos de `getHeatmap`/`getNetwork`/`getGenerational` — el backend no
   * sirve ninguna descarga, solo audita. 200 OK sin cuerpo de respuesta.
   *
   * El llamador debe tratarla como fire-and-forget: si falla, se loguea (guard DEV) pero NUNCA debe
   * bloquear la descarga del Excel, que ya se completó client-side antes de llamar aquí.
   */
  auditDemograficoExport: (scope: string | undefined, rowCount: number) =>
    request<void>(
      `/v1/analytics/recognition/demografico-export-audit${buildQuery({ scope, rowCount })}`,
      { method: 'POST' },
      ANALYTICS_BASE_URL,
    ),
};

/**
 * `permissions` viaja en el wire como arreglo de claves (espejo EXACTO de
 * PMP.SharedContracts.DTOs.Users.RecognitionAdminDto/UpdateRecognitionAdminRequest), nunca como
 * mapa — `AdminUserDto`/`UpdateUserRolesBody` (el mapa que consume el resto de la app) son la
 * forma de UI. Este adaptador es el único punto de conversión entre ambas formas.
 */
type AdminUserWireDto = Omit<AdminUserDto, 'permissions'> & { permissions: string[] };

function toPermissionMap(keys: readonly string[]): PermissionMap {
  const map = {} as PermissionMap;
  for (const key of PERMISSION_ORDER) map[key] = keys.includes(key);
  return map;
}

function toPermissionKeys(map: PermissionMap): string[] {
  return PERMISSION_ORDER.filter((key) => map[key]);
}

function toAdminUserDto(wire: AdminUserWireDto): AdminUserDto {
  return { ...wire, permissions: toPermissionMap(wire.permissions) };
}

// ─── UserAdminService — roles, registro frontline, avatar, legal (REQ-127/139/140/141) ───
export const userAdminApi = {
  /** GET v1/users/recognition-admins — administradores del módulo (tabla de Roles y Permisos). */
  getAdmins: () =>
    request<AdminUserWireDto[]>('/v1/users/recognition-admins', {}, USERS_BASE_URL)
      .then((admins) => admins.map(toAdminUserDto)),

  /**
   * GET v1/users/{id} — usuario por id, mapeado a PersonSummary. Se usa para resolver los nombres
   * del Wrapped (top receptor/emisor) desde el directorio real. Degrada con retry 0 en el hook.
   */
  getUserById: (id: string) =>
    request<{ id: string; email: string; displayName?: string | null; jobTitle?: string | null; ouId?: string | null; ouName?: string | null; photoUrl?: string | null; avatar?: string | null }>(
      `/v1/users/${encodeURIComponent(id)}`, {}, USERS_BASE_URL,
    ).then(toPersonSummary),

  /** GET v1/users/{id}/manager-status — ¿el usuario tiene reportes directos? (Mi Equipo). */
  getManagerStatus: (id: string) =>
    request<ManagerStatusDto>(
      `/v1/users/${encodeURIComponent(id)}/manager-status`, {}, USERS_BASE_URL,
    ),

  /** PUT v1/users/{id}/recognition-role — actualiza rol, alcance, estado y permisos (cascada). */
  updateUserRoles: (id: string, body: UpdateUserRolesBody) =>
    request<AdminUserWireDto>(`/v1/users/${encodeURIComponent(id)}/recognition-role`, {
      method: 'PUT',
      body: JSON.stringify({ ...body, permissions: toPermissionKeys(body.permissions) }),
    }, USERS_BASE_URL).then(toAdminUserDto),

  /** GET v1/users/org-catalogs?ou= — catálogos BU/site/depto para el registro (NO hardcodeados). */
  getFrontlineCatalogs: (ou: string) =>
    request<FrontlineCatalogs>(`/v1/users/org-catalogs${buildQuery({ ou })}`, {}, USERS_BASE_URL),

  /** GET v1/users/registration-ous — lista pública de OUs (id real + bandera Europa) para el dropdown del registro. */
  getRegistrationCatalogs: () =>
    request<RegistrationOu[]>('/v1/users/registration-ous', {}, USERS_BASE_URL),

  /** POST v1/users/frontline-login — login standalone por contraseña; emite un JWT propio (RF-002). */
  frontlineLogin: (body: FrontlineLoginBody) =>
    request<FrontlineLoginResult>('/v1/users/frontline-login', {
      method: 'POST',
      body: JSON.stringify(body),
    }, USERS_BASE_URL),

  /** POST v1/users/frontline-register — alta de un colaborador de línea. */
  frontlineRegister: (body: FrontlineRegisterBody) =>
    request<FrontlineRegisterResult>('/v1/users/frontline-register', {
      method: 'POST',
      body: JSON.stringify(body),
    }, USERS_BASE_URL),

  /** POST v1/users/frontline-activate — activa la cuenta (fija contraseña) del colaborador cargado por bulk. */
  frontlineActivate: (body: FrontlineActivateBody) =>
    request<FrontlineRegisterResult>('/v1/users/frontline-activate', {
      method: 'POST',
      body: JSON.stringify(body),
    }, USERS_BASE_URL),

  /**
   * POST v1/users/bulk-import — carga masiva del padrón Frontline por CSV (RF-010/RF-114).
   * Idempotente por correo; scope estricto server-side. Body { csv }. Requiere sesión Admin/Supervisor.
   * Columnas: email,nombre,ouId,buId,siteId,depto,employeeId,hireDate,preferredLanguage.
   */
  bulkImportUsers: (csv: string) =>
    request<BulkImportUsersResultDto>('/v1/users/bulk-import', {
      method: 'POST',
      body: JSON.stringify({ csv }),
    }, USERS_BASE_URL),

  /**
   * POST v1/users/europe-usa-import — carga masiva del padron de Europa y EE. UU. (PLAN WP-U6).
   * Escribe en `users`, a diferencia de europe-import, que escribia en una coleccion aparte que
   * nadie mas leia. Con preview=true valida el archivo entero y NO escribe nada.
   */
  europeUsaImport: (csv: string, preview: boolean, ou: string) =>
    request<EuropeUsaImportResultDto>(`/v1/users/europe-usa-import?preview=${preview}`, {
      method: 'POST',
      body: JSON.stringify({ csv, preview, ou }),
    }, USERS_BASE_URL),

  /** GET v1/users/europe-usa-import/template — la plantilla la sirve el backend (D-U6). */
  europeUsaImportTemplate: () =>
    requestText('/v1/users/europe-usa-import/template', undefined, USERS_BASE_URL),

  /** GET v1/admin/sync-settings — fuentes de sincronizacion del tenant. */
  getSyncSettings: () =>
    request<SyncSettingsDto>('/v1/admin/sync-settings', undefined, USERS_BASE_URL),

  /**
   * PUT v1/admin/sync-settings.
   *
   * 🔴 `USERS_BASE_URL` no es opcional: el tercer parámetro de `request` cae por defecto en
   * `RECOGNITION_BASE_URL`, y sin él este PUT se iba al RecognitionService —donde la ruta no
   * existe—, así que **ningún** guardado de la tarjeta «Fuente de datos» llegaba nunca al
   * UserAdminService. El GET de al lado sí lo pasaba, y por eso la pantalla cargaba bien y sólo
   * fallaba al guardar. Ver `client.syncSettings.test.ts`.
   */
  saveSyncSettings: (body: SaveSyncSettingsBody) =>
    request<SyncSettingsDto>('/v1/admin/sync-settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    }, USERS_BASE_URL),

  /**
   * POST v1/users/europe-import — carga masiva del padrón Europa (solo AdminGlobal, tenant Europa).
   * Columnas: email,nombre,employeeId,hireDate,ouId,buId,siteId,depto,country.
   */
  europeImportUsers: (csv: string) =>
    request<BulkImportUsersResultDto>('/v1/users/europe-import', {
      method: 'POST',
      body: JSON.stringify({ csv }),
    }, USERS_BASE_URL),

  /** GET v1/users/verify-email?token= — confirma la verificación del correo del alta. */
  verifyEmail: (token: string) =>
    request<VerifyEmailResult>(`/v1/users/verify-email${buildQuery({ token })}`, {}, USERS_BASE_URL),

  /**
   * POST v1/users/resend-verification — vuelve a mandar el correo de verificación.
   *
   * 🔴 Responde **202 siempre**, exista o no la cuenta y esté verificada o no. La UI no puede
   * deducir nada de la respuesta, y eso es intencional: si distinguiera, el formulario sería un
   * buscador de qué correos están dados de alta en Sigma.
   */
  resendVerification: (correo: string) =>
    request<void>('/v1/users/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ correo }),
    }, USERS_BASE_URL),

  /** POST v1/users/forgot-password — pide el enlace de restablecimiento. 202 opaco, igual que el de arriba. */
  forgotPassword: (correo: string) =>
    request<void>('/v1/users/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ correo }),
    }, USERS_BASE_URL),

  /**
   * POST v1/users/reset-password — fija la contraseña nueva con el token del correo.
   * 204 si se restableció; 400 **genérico** si el token no sirve — caducado, ya usado o inexistente
   * dan el mismo mensaje, así que la pantalla no puede (ni debe) distinguirlos.
   */
  resetPassword: (token: string, password: string) =>
    request<void>('/v1/users/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }, USERS_BASE_URL),

  /**
   * GET v1/users/avatars — catálogo DINÁMICO de avatares Frontline (RF-008). Endpoint anónimo:
   * devuelve los blobs disponibles (id = nombre del blob; url = URL con SAS lista para `<img src>`).
   * Pueden ser 1 o N.
   */
  getFrontlineAvatars: () =>
    request<FrontlineAvatar[]>('/v1/users/avatars', {}, USERS_BASE_URL),

  /**
   * GET v1/users/{id}/profile — perfil del usuario, **el único endpoint que devuelve `avatar`**
   * (RF-008). Se llama a éste y no a `resolve`: `UserDto` está congelado y no lleva `Avatar`, así que
   * meterlo ahí arrastraría a SharedContracts y a todos los servicios que lo consumen por un campo
   * que sólo necesita una pantalla.
   */
  getUserProfile: (id: string) =>
    request<UserProfile>(`/v1/users/${encodeURIComponent(id)}/profile`, {}, USERS_BASE_URL),

  /** PATCH v1/users/{id}/avatar — persiste el avatar elegido (id del catálogo dinámico, RF-008). */
  // 🔴 Devuelve `UserProfile`, no `{ success: boolean }`. El backend responde con el perfil completo
  // (`UserProfileDto`, ver `SetUserAvatarCommandHandler`), así que quien leyera `data.success` obtenía
  // `undefined` — un tipo que mentía, no un detalle de estilo.
  updateAvatar: (id: string, body: UpdateAvatarBody) =>
    request<UserProfile>(`/v1/users/${encodeURIComponent(id)}/avatar`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }, USERS_BASE_URL),

  /** GET v1/legal-acceptances/status — documentos legales pendientes de (re)aceptar. */
  getLegalStatus: () =>
    request<LegalAcceptanceStatus>('/v1/legal-acceptances/status', {}, USERS_BASE_URL),

  /** GET v1/legal-documents/{type}?lang= — contenido versionado del documento legal (no hardcodeado). */
  getLegalDocument: (type: LegalDocType, lang: string, ouId?: string) =>
    request<LegalDocumentDto>(
      // `ouId` decide QUÉ PDF corporativo alcanza a esta persona (por alcance o por ancestros).
      // Vacío = el servidor resuelve sin filtrar por OU: el enlace del aviso de privacidad se abre
      // antes de elegir organización.
      `/v1/legal-documents/${encodeURIComponent(type)}${buildQuery({ lang, ouId })}`,
      {},
      USERS_BASE_URL,
    ),

  /** GET v1/legal-documents/{type}/draft — borrador editable (admin). */
  getLegalDocumentDraft: (type: LegalDocType) =>
    request<LegalDocumentDraftDto>(`/v1/legal-documents/${encodeURIComponent(type)}/draft`, {}, USERS_BASE_URL),

  /** PUT v1/legal-documents/{type}/draft — guarda el borrador (admin, sin exigir idiomas completos). */
  saveLegalDocumentDraft: (type: LegalDocType, body: SaveLegalDocumentDraftBody) =>
    request<LegalDocumentDraftDto>(`/v1/legal-documents/${encodeURIComponent(type)}/draft`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }, USERS_BASE_URL),

  /** POST v1/legal-documents/{type}/publish — publica el borrador como versión vigente (admin). */
  publishLegalDocument: (type: LegalDocType, body: PublishLegalDocumentBody) =>
    request<{ tenantId: string; documentKey: string; version: string; isCurrent: boolean; effectiveAt: string }>(
      `/v1/legal-documents/${encodeURIComponent(type)}/publish`,
      { method: 'POST', body: JSON.stringify(body) },
      USERS_BASE_URL,
    ),

  /** POST v1/legal-acceptances — registra la aceptación de una versión concreta (ARCO). */
  acceptLegal: (body: LegalAcceptanceBody & { userId: string }) =>
    request<{ success: boolean }>('/v1/legal-acceptances', {
      method: 'POST',
      body: JSON.stringify({
        userId: body.userId,
        documentKey: body.type,
        version: body.version,
        // Qué se mostró (`pdf-…`). El servidor lo guarda como evidencia; la compuerta compara `version`.
        displayedVersion: body.displayedVersion ?? null,
      }),
    }, USERS_BASE_URL),

  // ─── RF-079 — documentos PDF corporativos (repositorio por OU, §11) ───
  /** GET v1/corporate-documents — listado de administración (DTO completo). */
  listCorporateDocuments: () =>
    request<CorporateDocumentDto[]>('/v1/corporate-documents', {}, USERS_BASE_URL),

  /** GET v1/corporate-documents/for-me?lang= — documentos visibles para el usuario (DTO reducido). */
  getCorporateDocumentsForMe: (lang: string) =>
    request<CorporateDocumentForMeDto[]>(
      `/v1/corporate-documents/for-me${buildQuery({ lang })}`, {}, USERS_BASE_URL,
    ),

  /** POST v1/corporate-documents — sube un documento (multipart). PDF envía `file`; Video/Imagen envían
   *  `mediaKind` + `externalUrl` (sin archivo). */
  uploadCorporateDocument: (input: UploadCorporateDocumentInput) => {
    const form = new FormData();
    if (input.file) form.append('file', input.file);
    form.append('nombre', input.nombre);
    form.append('tipo', input.tipo);
    input.scopeOuIds.forEach((id) => form.append('scopeOuIds', id));
    input.languages.forEach((code) => form.append('languages', code));
    // GAP-DOC — banderas de la tabla legal (Primer login / Detonar).
    if (input.firstLogin) form.append('firstLogin', 'true');
    if (input.triggerUpdate) form.append('triggerUpdate', 'true');
    // Biblioteca: tipo de medio + URL externa (Video/Imagen).
    form.append('mediaKind', input.mediaKind ?? 'Pdf');
    if (input.externalUrl) form.append('externalUrl', input.externalUrl);
    return requestUpload<CorporateDocumentDto>('/v1/corporate-documents', form, USERS_BASE_URL);
  },

  /** PUT v1/corporate-documents/{id} — actualiza metadata (sin reemplazar el archivo). */
  updateCorporateDocument: (id: string, body: UpdateCorporateDocumentBody) =>
    request<CorporateDocumentDto>(`/v1/corporate-documents/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }, USERS_BASE_URL),

  /** DELETE v1/corporate-documents/{id} — baja del documento (204). */
  deleteCorporateDocument: (id: string) =>
    request<void>(`/v1/corporate-documents/${encodeURIComponent(id)}`, { method: 'DELETE' }, USERS_BASE_URL),

  /** POST v1/corporate-documents/{id}/files/{lang} — sube el PDF de un idioma (Versión ES/EN). */
  uploadCorporateDocumentFile: (id: string, lang: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return requestUpload<CorporateDocumentDto>(
      `/v1/corporate-documents/${encodeURIComponent(id)}/files/${encodeURIComponent(lang)}`,
      form, USERS_BASE_URL,
    );
  },

  /** DELETE v1/corporate-documents/{id}/files/{lang} — quita el PDF de un idioma. */
  deleteCorporateDocumentFile: (id: string, lang: string) =>
    request<CorporateDocumentDto>(
      `/v1/corporate-documents/${encodeURIComponent(id)}/files/${encodeURIComponent(lang)}`,
      { method: 'DELETE' }, USERS_BASE_URL,
    ),
};

// ─── RulesEngine — configuración de puntos (REQ-128) ────────────────────────────

/** Código de la BusinessRule del kill-switch global. Espejo de `RuleCodes.PointsEnabledGlobal`. */
export const POINTS_ENABLED_GLOBAL = 'POINTS_ENABLED_GLOBAL';

/**
 * WP-A5 · RN-P09 — código de la regla «Visible para empleados». Espejo de
 * `RuleCodes.PointsVisibleToEmployees`.
 *
 * 🔴 Es una regla POR OU, no por segmento: se administra con un override
 * (`PATCH v1/rules/{code}/ou-overrides/{ouId}`), no con la config de puntos. Ponerla en el bloque de
 * cada segmento la pintaría dos veces —staff y frontline— mostrando y escribiendo el mismo valor.
 */
export const POINTS_VISIBLE_TO_EMPLOYEES = 'POINTS_VISIBLE_TO_EMPLOYEES';

export const rulesApi = {
  /** GET v1/rules/points-config/{ouId}?segment= — config de puntos de la OU por segmento. */
  getPointsConfig: (ouId: string, segment: PointsSegment) =>
    request<PointsConfigDto>(
      `/v1/rules/points-config/${encodeURIComponent(ouId)}${buildQuery({ segment })}`,
      {},
      RULES_BASE_URL,
    ),

  /** PUT v1/rules/points-config/{ouId}?segment= — guarda la config (bloqueada si CampaignStarted). */
  savePointsConfig: (ouId: string, segment: PointsSegment, body: PointsConfigDto) =>
    request<PointsConfigDto>(
      `/v1/rules/points-config/${encodeURIComponent(ouId)}${buildQuery({ segment })}`,
      { method: 'PUT', body: JSON.stringify(body) },
      RULES_BASE_URL,
    ),

  /** POST v1/rules/points-config/{ouId}/reset?segment= — re-establece a 0 el Total Acumulado. */
  resetPointsConfig: (ouId: string, segment: PointsSegment) =>
    request<PointsConfigDto>(
      `/v1/rules/points-config/${encodeURIComponent(ouId)}/reset${buildQuery({ segment })}`,
      { method: 'POST' },
      RULES_BASE_URL,
    ),

  /**
   * WP-A4 / RF-039 — GET v1/rules/points-config/summary?ouId=&segment=
   *
   * El conteo de nominaciones por criterio de la columna "Total Acumulado". El backend decide el
   * alcance a partir de los claims, así que `ouId` es una PETICIÓN, no una orden: a un admin no
   * global se le ignora y se le confina a la suya.
   */
  getPointsSummary: (ouId: string | undefined, segment: PointsSegment | undefined) =>
    request<PointsConfigSummaryDto>(
      `/v1/rules/points-config/summary${buildQuery({ ouId, segment })}`,
      {},
      RULES_BASE_URL,
    ),

  /**
   * WP-A3 — GET v1/rules/{code}: la regla EN CRUDO, con su valor base y sus overrides por OU.
   *
   * 🔴 Hace falta además del toggle resuelto. El control edita el VALOR BASE, mientras
   * `points-config/toggle` devuelve el valor YA RESUELTO para una OU concreta —con su override
   * aplicado—. Pintar el resuelto en un control que escribe el base es lo que hacía que sobre Europa
   * (que tiene un override de arranque a `true`) el admin apagara el interruptor y siguiera viéndolo
   * encendido, sin ningún error.
   */
  getRule: (code: string) =>
    request<BusinessRuleDto>(`/v1/rules/${encodeURIComponent(code)}`, {}, RULES_BASE_URL),

  /** WP-A3 / REQ-038 — GET v1/rules/points-config/toggle?ouId=&segment= */
  getPointsToggle: (ouId: string, segment: PointsSegment) =>
    request<PointsToggleDto>(
      `/v1/rules/points-config/toggle${buildQuery({ ouId, segment })}`,
      {},
      RULES_BASE_URL,
    ),

  /**
   * WP-A3 / D-1 — el kill-switch GLOBAL de puntos (PUT v1/rules/POINTS_ENABLED_GLOBAL).
   *
   * Es una BusinessRule, no un campo de la config por OU. Solo AdminGlobal: lo exige el backend con
   * su propio `[Authorize]`, no esta capa.
   *
   * 🔴 Se manda `value` Y `isActive`, y los dos importan:
   * `BusinessRule.IsEnabledForOu` es `IsActive && bool.Parse(EffectiveValueForOu(ou))`. Mandar solo
   * `isActive` dejaba el `Value` intacto, así que apagar y encender el interruptor podía no cambiar
   * nada — un control que parece funcionar y no hace nada. Además `UpdateRuleRequest.Value` es
   * posicional y no admite null.
   *
   * ⚠️ Los OVERRIDES POR OU siguen mandando sobre este valor base (Europa tiene uno de arranque),
   * así que apagar el global no garantiza que TODAS las OUs queden apagadas. Eso se administra por
   * `PATCH v1/rules/{code}/ou-overrides/{ouId}`, que no es esta pantalla.
   */
  setGlobalPointsEnabled: (enabled: boolean) =>
    request<unknown>(
      `/v1/rules/${encodeURIComponent(POINTS_ENABLED_GLOBAL)}`,
      { method: 'PUT', body: JSON.stringify({ value: String(enabled), isActive: true }) },
      RULES_BASE_URL,
    ),

  /**
   * WP-A5 — PATCH v1/rules/{code}/ou-overrides/{ouId}: el valor de una regla PARA UNA OU.
   *
   * 🔴 `value: null` ELIMINA el override; no lo pone en `false`. Son cosas distintas y el backend
   * las distingue (`UpsertRuleOuOverrideCommandHandler`): sin override, la OU hereda el valor base;
   * con override en `false`, la OU dice que no aunque el base diga que sí. Mandar `null` creyendo
   * que apaga es la confusión que este comentario existe para evitar.
   *
   * El servidor valida el tipo: una regla `bool` rechaza cualquier cosa que no parsee como booleano.
   * Y los roles los exige él (`AdminGlobal,AdminPulso`), no esta capa.
   */
  setOuRuleOverride: (code: string, ouId: string, value: string | null) =>
    request<BusinessRuleDto>(
      `/v1/rules/${encodeURIComponent(code)}/ou-overrides/${encodeURIComponent(ouId)}`,
      { method: 'PATCH', body: JSON.stringify({ value }) },
      RULES_BASE_URL,
    ),

  /**
   * WP-A6 / RF-066 — GET v1/rules/points-export?ouId=&page=&pageSize=: la tabla de puntos por
   * persona, de solo lectura (Persona/OU/Puntos). Primer consumidor de este endpoint — hasta esta
   * tarea existía y funcionaba en el backend, pero nadie podía descargarlo desde el front.
   *
   * `ouId` es una PETICIÓN, igual que en `getPointsSummary`: el backend decide el alcance real por
   * los claims del admin y confina a uno no global a su propia OU. `pageSize` tope 1000 en el
   * servidor — el consumidor (`AdminPoints.tsx`) recorre páginas con el mismo patrón de
   * paginado-con-tope que `AdminWinners.exportar()`.
   */
  exportPersonPoints: (ouId: string | undefined, page = 1, pageSize = 200) =>
    request<PointsExportPageDto>(
      `/v1/rules/points-export${buildQuery({ ouId, page, pageSize })}`,
      {},
      RULES_BASE_URL,
    ),
};

// ─── NotificationService — bandeja in-app + preferencias self-service (REQ-071/070) ──
// Rutas congeladas en ApiRoutes.Notifications: Inbox, InboxRead (.../inbox/{id}/read),
// InboxReadAll (.../inbox/read-all) y SelfPreferences (.../recognition/me/preferences).
// Las mutaciones responden 204 No Content (o { updated }); el request las mapea a undefined.
export const notificationsApi = {
  /** GET v1/notifications/inbox — bandeja del usuario + contador de no leídas (paginada). */
  getInbox: (page = 1, pageSize = 20) =>
    request<NotificationInboxResponse>(
      `/v1/notifications/inbox${buildQuery({ page, pageSize })}`,
      {},
      NOTIFICATIONS_BASE_URL,
    ),

  /** POST v1/notifications/inbox/{id}/read — marca una notificación como leída (204). */
  markRead: (id: string) =>
    request<void>(
      `/v1/notifications/inbox/${encodeURIComponent(id)}/read`,
      { method: 'POST' },
      NOTIFICATIONS_BASE_URL,
    ),

  /** POST v1/notifications/inbox/{id}/unread — devuelve una notificación a NO leída (204) — RF-164. */
  markUnread: (id: string) =>
    request<void>(
      `/v1/notifications/inbox/${encodeURIComponent(id)}/unread`,
      { method: 'POST' },
      NOTIFICATIONS_BASE_URL,
    ),

  /** POST v1/notifications/inbox/read-all — marca todas como leídas (limpia el badge). */
  markAllRead: () =>
    request<{ updated: number }>(
      '/v1/notifications/inbox/read-all',
      { method: 'POST' },
      NOTIFICATIONS_BASE_URL,
    ),

  // ── RF-162 — Configuración jerárquica de admin (Site > BU > OU > Global) ──────

  /**
   * GET v1/notifications/recognition/settings?scope=&scopeId= — documento CRUDO de UN nivel, con sus
   * null de herencia intactos. Es el que edita el panel de administración; `/resolve` (abajo) devuelve
   * la mezcla ya calculada y guardar sobre ella aplanaría la herencia.
   */
  getRecognitionSettings: (scope: PreferenceScope, scopeId?: string | null) =>
    request<RecognitionPreferenceDto>(
      `/v1/notifications/recognition/settings${buildQuery({ scope, scopeId: scopeId ?? undefined })}`,
      {},
      NOTIFICATIONS_BASE_URL,
    ),

  /** PUT v1/notifications/recognition/settings — guarda el documento completo de UN nivel (204). */
  saveRecognitionSettings: (body: SaveRecognitionPreferenceRequest) =>
    request<void>(
      '/v1/notifications/recognition/settings',
      { method: 'PUT', body: JSON.stringify(body) },
      NOTIFICATIONS_BASE_URL,
    ),

  /**
   * WP-F4-b (6.3) — GET v1/notifications/recognition/rows — las filas de las dos tablas, con su
   * asunto en los siete idiomas y el estado de sus tres interruptores EN ESTE NIVEL.
   */
  getRecognitionRows: (scope: PreferenceScope, scopeId?: string | null) =>
    request<NotificationRowDto[]>(
      `/v1/notifications/recognition/rows${buildQuery({ scope, scopeId: scopeId ?? undefined })}`,
      {},
      NOTIFICATIONS_BASE_URL,
    ),

  /**
   * WP-F4-b (6.4) — POST .../recognition/broadcast — «Avisos del sistema».
   *
   * La audiencia se resuelve en el SERVIDOR: la pantalla dice «todos» o «esta OU» y no descarga el
   * padrón para construir una lista de correos, que es lo que hace el camino antiguo de Pulso.
   */
  sendSystemBroadcast: (body: SystemBroadcastRequest) =>
    request<SystemBroadcastResult>(
      '/v1/notifications/recognition/broadcast',
      { method: 'POST', body: JSON.stringify(body) },
      NOTIFICATIONS_BASE_URL,
    ),

  /**
   * WP-F4-b (6.3) — PUT .../rows/{key}/subject — el asunto de una fila, por idioma.
   *
   * Devuelve cuántas plantillas se tocaron, y no un 204 mudo: cero es un desenlace posible —una
   * plantilla que el sembrador todavía no creó— y hay que poder distinguirlo de «guardado».
   */
  saveRecognitionRowSubject: (templateKey: string, subjects: Record<string, string>) =>
    request<{ updated: number }>(
      `/v1/notifications/recognition/rows/${encodeURIComponent(templateKey)}/subject`,
      { method: 'PUT', body: JSON.stringify({ subjects }) },
      NOTIFICATIONS_BASE_URL,
    ),

  /**
   * GET v1/notifications/recognition/settings/resolve — configuración EFECTIVA de un destinatario tras
   * aplicar Site>BU>OU>Global. Sirve para previsualizar en el panel qué recibirá realmente alguien de
   * ese scope, sin tener que deducir la mezcla a mano.
   */
  resolveRecognitionSettings: (params: {
    siteId?: string | null; buId?: string | null; ouId?: string | null; email?: string | null;
  }) =>
    request<ResolvedPreferencesDto>(
      `/v1/notifications/recognition/settings/resolve${buildQuery({
        siteId: params.siteId ?? undefined,
        buId: params.buId ?? undefined,
        ouId: params.ouId ?? undefined,
        email: params.email ?? undefined,
      })}`,
      {},
      NOTIFICATIONS_BASE_URL,
    ),

  /** GET v1/notifications/recognition/me/preferences — preferencias self-service del usuario (REQ-070). */
  getSelfPreferences: () =>
    request<NotificationSelfPreferenceDto>(
      '/v1/notifications/recognition/me/preferences',
      {},
      NOTIFICATIONS_BASE_URL,
    ),

  /** PUT v1/notifications/recognition/me/preferences — guarda las preferencias (204). El backend ignora userId. */
  saveSelfPreferences: (body: NotificationSelfPreferenceDto) =>
    request<void>(
      '/v1/notifications/recognition/me/preferences',
      { method: 'PUT', body: JSON.stringify(body) },
      NOTIFICATIONS_BASE_URL,
    ),
};

// ─── RecognitionService — Documentation Pills del rail (CPE-162/163/164) ─────────
// (El catálogo de Fundamentales de RecognitionService se retiró — P2-010: el catálogo único
//  vive en SurveyService. Este objeto conserva solo las Documentation Pills del rail.)
export const catalogApi = {
  // ─── FASE 4 (§24.4) — pills de acceso rápido del rail (CPE-162/163/164) ───
  /** GET v1/documentation-pills — listado de administración (activas e inactivas). */
  getPills: () => request<DocumentationPillDto[]>('/v1/documentation-pills'),

  /** GET v1/documentation-pills/for-me — pills activas visibles para el usuario (alimenta el rail). */
  getPillsForMe: () => request<DocumentationPillDto[]>('/v1/documentation-pills/for-me'),

  /** POST v1/documentation-pills — alta de una pill. */
  createPill: (body: UpsertPillBody) =>
    request<DocumentationPillDto>('/v1/documentation-pills', { method: 'POST', body: JSON.stringify(body) }),

  /** PUT v1/documentation-pills/{id} — edición de una pill. */
  updatePill: (id: string, body: UpsertPillBody) =>
    request<DocumentationPillDto>(`/v1/documentation-pills/${encodeURIComponent(id)}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),

  /** DELETE v1/documentation-pills/{id} — baja de una pill. */
  deletePill: (id: string) =>
    request<{ success: boolean }>(`/v1/documentation-pills/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

// ─── RecognitionService — Moderación (REQ-130) ──────────────────────────────────
export const moderationApi = {
  /** GET v1/moderation/reports?status= — bandeja FIFO (Pendientes primero). */
  getReports: (status?: string) =>
    request<ReportDto[]>(`/v1/moderation/reports${buildQuery({ status })}`),

  /**
   * GET v1/moderation/report — Reportería de Moderación (WP-08, Fase 3): KPIs agregados
   * (total/pendientes/ocultos/restaurados) + detalle de casos, para el tab de Reportería. Vive en
   * RecognitionService (mismo host que el resto de `moderationApi`), no en AnalyticsService — la
   * colección `reports` con datos de contacto ya resueltos vive ahí, sin necesidad de un viaje HTTP
   * cross-servicio.
   *
   * Acepta los filtros de la tira de Reportería SALVO Fundamental — ver `toModerationReportQuery`.
   */
  getReport: (params: RecognitionReportParams = {}) =>
    request<ModerationReportDto>(
      `/v1/moderation/report${buildQuery(toModerationReportQuery(params))}`,
    ),

  /**
   * G2/G5 — POST v1/moderation/report-export-audit?rowCount=: audita (bitácora propia de
   * RecognitionService) que el export "07 - Moderacion.xlsx" se descargó. El Excel lo arma el
   * FRONTEND client-side con los datos ya traídos de `getReport` — el backend no sirve ninguna
   * descarga, solo audita. Sin `scope`: el backend lo calcula del propio token del actor — lo que la
   * bitácora tiene que registrar es el ALCANCE que autorizó la descarga, no los filtros que quien
   * exporta eligió encima (esos solo pueden acotar dentro de ese alcance).
   *
   * Fire-and-forget (mismo patrón que `auditDemograficoExport`/`auditLeadersListExport`): si falla,
   * se loguea (guard DEV) pero NUNCA debe bloquear la descarga del Excel, que ya se completó
   * client-side antes de llamar aquí.
   */
  auditReportExport: (rowCount: number) =>
    request<void>(`/v1/moderation/report-export-audit${buildQuery({ rowCount })}`, { method: 'POST' }),

  /** GET v1/moderation/reports/{id} — detalle con datos de canalización (ReportDetailDto). */
  getReportDetail: (id: string) =>
    request<ReportDetailDto>(`/v1/moderation/reports/${encodeURIComponent(id)}`),

  /** POST v1/moderation/{id}/hide — confirma el reporte (Resuelto + reversa). */
  hide: (id: string) =>
    request<{ success: boolean }>(`/v1/moderation/${encodeURIComponent(id)}/hide`, { method: 'POST' }),

  /** POST v1/moderation/{id}/restore — desestima el reporte (vuelve visible). */
  restore: (id: string) =>
    request<{ success: boolean }>(`/v1/moderation/${encodeURIComponent(id)}/restore`, { method: 'POST' }),

  /** POST v1/moderation/{id}/reopen — deshace un "Restaurar": el caso vuelve a Pendiente. */
  reopen: (id: string) =>
    request<{ success: boolean }>(`/v1/moderation/${encodeURIComponent(id)}/reopen`, { method: 'POST' }),

  /** GET v1/moderation/blocked-words?country=&language= — catálogo por país/idioma. */
  getBlockedWords: (country?: string, language?: string) =>
    request<BlockedWordDto[]>(`/v1/moderation/blocked-words${buildQuery({ country, language })}`),

  /** POST v1/moderation/blocked-words — alta de palabra bloqueada. */
  createBlockedWord: (body: BlockedWordBody) =>
    request<BlockedWordDto>('/v1/moderation/blocked-words', { method: 'POST', body: JSON.stringify(body) }),

  /** PUT v1/moderation/blocked-words/{id} — edición en línea de una palabra bloqueada. */
  updateBlockedWord: (id: string, body: BlockedWordBody) =>
    request<BlockedWordDto>(`/v1/moderation/blocked-words/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /** DELETE v1/moderation/blocked-words/{id} — baja de palabra bloqueada. */
  deleteBlockedWord: (id: string) =>
    request<{ success: boolean }>(`/v1/moderation/blocked-words/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /**
   * GET v1/moderation/reports/{id}/notify-admin/preview — a quién llegaría el ✉️ (RF-090).
   *
   * Solo lectura, mismo permiso y mismo acotado por scope que el POST. Alimenta el modal de
   * confirmación: sin esto el moderador confirmaba a ciegas y el acuse verde no probaba nada.
   */
  notifyAdminPreview: (reportId: string) =>
    request<ModerationNotifyPreviewDto>(
      `/v1/moderation/reports/${encodeURIComponent(reportId)}/notify-admin/preview`),

  /**
   * POST v1/moderation/reports/{id}/notify-admin — escala el caso al admin de la OU/país (RF-090).
   *
   * `success: true` significa «hay al menos un destinatario válido y el aviso quedó encolado».
   * Sin destinatario responde 422 con el código del motivo en `errors.reason`.
   */
  notifyAdmin: (reportId: string) =>
    request<{ success: boolean; recipients: number }>(
      `/v1/moderation/reports/${encodeURIComponent(reportId)}/notify-admin`, { method: 'POST' }),

  /** GET v1/moderation/country-languages — tabla país → idiomas hablados (RF-082). */
  getCountryLanguages: () =>
    request<CountryLanguageMapDto>('/v1/moderation/country-languages'),

  /** PUT v1/moderation/country-languages — edita la tabla (solo alcance global). */
  saveCountryLanguages: (body: CountryLanguageMapDto) =>
    request<CountryLanguageMapDto>('/v1/moderation/country-languages', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /** GET v1/view-settings/moderation?ouId= — toggles de moderación (ModerationSettingsDto). */
  getSettings: (ouId?: string) =>
    request<ModerationSettingsDto>(`/v1/view-settings/moderation${buildQuery({ ouId })}`),

  /** PUT v1/view-settings/moderation — guarda los toggles (solo moderadores/admin). */
  saveSettings: (body: ModerationSettingsDto) =>
    request<ModerationSettingsDto>('/v1/view-settings/moderation', { method: 'PUT', body: JSON.stringify(body) }),
};

// ─── WP-F3 / REQ-136 — Vistas de Usuario ────────────────────────────────────────
export const userViewsApi = {
  /**
   * GET v1/view-settings/user-views?ouId=&own= — vistas de esa OU.
   * `own` pide el documento PROPIO del nivel (admin); sin él, el EFECTIVO (colaborador).
   */
  get: (ouId?: string, own = false) =>
    request<UserViewSettingsDto>(`/v1/view-settings/user-views${buildQuery({ ouId, own: own ? 'true' : undefined })}`),

  /** PUT v1/view-settings/user-views?ouId= — el nivel va en la QUERY, nunca en el cuerpo. */
  save: (body: UserViewSettingsDto, ouId?: string) =>
    request<UserViewSettingsDto>(`/v1/view-settings/user-views${buildQuery({ ouId })}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
};

// ─── RecognitionService — Ganadores (REQ-131) ───────────────────────────────────
export const winnersApi = {
  /**
   * GET v1/winners/badges — el catálogo de insignias que ESTA pantalla puede leer.
   *
   * 🔴 No se usa `gestionApi.getBadges()` a propósito: ese endpoint está gateado con
   * `[Authorize(Roles = "AdminGlobal")]`, y la pantalla de Ganadores la usan Admin OU/BU y Admin
   * Local País, que entran por capacidad. Pedirle aquél les daría un 403 y un desplegable vacío sin
   * explicación — el mismo defecto que se midió en WP-A5.
   *
   * `categoria` es opcional y filtra server-side:
   * - Sin él, el backend devuelve TODAS las categorías (Streak + Campaign) — la columna de la tabla
   *   necesita esto para resolver el nombre de CUALQUIER ganador, tenga insignia de racha o de
   *   campaña (hallazgo Important de la revisión final: antes solo se pedían insignias Campaign y un
   *   ganador con insignia de racha mostraba el id crudo).
   * - Con `'Campaign'`, el backend filtra solo insignias de campaña — el selector de asignación del
   *   cajón de edición y el catálogo administrable de "Insignias de Campaña" (grid + modal) NUNCA
   *   deben poder ofrecer ni listar una insignia de racha.
   */
  getBadges: (categoria?: 'Campaign') =>
    request<AdminBadgeDto[]>(`/v1/winners/badges${buildQuery({ categoria })}`),

  /** POST v1/winners/badges — crea una insignia de campaña (el backend fuerza categoria=Campaign). */
  createBadge: (badge: AdminBadgeDto) =>
    request<AdminBadgeDto>('/v1/winners/badges', { method: 'POST', body: JSON.stringify(badge) }),

  /** PUT v1/winners/badges/{id} — edita una insignia de campaña existente (404 si no es Campaign). */
  updateBadge: (id: string, badge: AdminBadgeDto) =>
    request<AdminBadgeDto>(`/v1/winners/badges/${encodeURIComponent(id)}`, {
      method: 'PUT', body: JSON.stringify(badge),
    }),

  /** DELETE v1/winners/badges/{id} — 404 si no es Campaign, 422 si algún ganador ya la tiene otorgada. */
  deleteBadge: (id: string) =>
    request<void>(`/v1/winners/badges/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /** POST v1/winners/badges/{id}/image — sube el ícono de una insignia de campaña ya persistida. */
  uploadBadgeImage: (badgeId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return requestUpload<AdminBadgeDto>(
      `/v1/winners/badges/${encodeURIComponent(badgeId)}/image`, form, RECOGNITION_BASE_URL);
  },

  /** GET v1/winners?campaignId=&ouId= — lista de ganadores. */
  getWinners: (campaignId?: string, ouId?: string) =>
    request<WinnerDto[]>(`/v1/winners${buildQuery({ campaignId, ouId })}`),

  /**
   * RF-157 / WP-D1 — GET v1/winners/search: el listado del admin, filtrado y PAGINADO en servidor.
   *
   * 🔴 El alcance NO se manda desde aquí. Lo resuelve el backend a partir de los claims: mandarlo
   * desde el cliente sería pedirle al navegador que decida a quién puede ver quien pregunta.
   */
  search: (params: WinnerSearchParams) =>
    request<WinnersPageDto>(`/v1/winners/search${buildQuery({ ...params })}`),

  /** POST v1/winners — registra/actualiza un ganador (idempotente por campaña+usuario). */
  register: (body: RegisterWinnerBody) =>
    request<WinnerDto>('/v1/winners', { method: 'POST', body: JSON.stringify(body) }),

  /** PUT v1/winners/{id} — edita un ganador. 403 si la ventana de edición está cerrada. */
  update: (id: string, body: RegisterWinnerBody) =>
    request<WinnerDto>(`/v1/winners/${encodeURIComponent(id)}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),

  /** POST v1/winners/{id}/confirm — Pendiente → Confirmado (CPE-134). */
  confirm: (id: string) =>
    request<{ success: boolean; cambio: boolean }>(
      `/v1/winners/${encodeURIComponent(id)}/confirm`, { method: 'POST' }),

  /**
   * POST v1/winners/import — sube el padrón de Ganadores (D-10).
   *
   * 🔴 Va como `multipart/form-data` con el ARCHIVO, no como texto. Un `.xlsx` es un ZIP binario:
   * leerlo con `file.text()` y mandarlo como cadena lo destruye antes de que el backend lo abra —
   * es el mismo error que ya se corrigió aquí mismo al detectar la firma del fichero decodificando
   * UTF-8, donde `D0 CF` se convertía en `U+FFFD` y la comparación no podía acertar jamás.
   *
   * La CAMPAÑA viaja aparte porque las seis columnas de la maqueta no la traen y el backend la
   * necesita: la identidad de un ganador es (campaña, persona).
   *
   * `modo` (Fase 3, carga histórica) distingue el padrón de 6 columnas de siempre ("vigente") del
   * padrón reducido de 3 columnas para años ya cerrados ("historica"). El backend lo valida con
   * `ImportWinnersModo.TryNormalize` y responde 422 ante cualquier otro valor.
   */
  /**
   * POST v1/winners/import — sube el padrón de Ganadores (D-10 / Fase 3).
   *
   * 🔴 Va como `multipart/form-data` con el ARCHIVO, no como texto — ver el comentario largo de
   * arriba sobre por qué (`.xlsx` es un ZIP binario).
   *
   * `campaignId` obligatorio SOLO en modo "vigente". `badgeId` es obligatorio en LOS DOS modos
   * (ajuste de UX post-lanzamiento: antes solo en "historica" y un ganador vigente nacía sin
   * insignia) — se elige una vez para todo el lote, ya no viaja en el Excel.
   */
  importPadron: (body: ImportWinnersBody) => {
    const form = new FormData();
    form.append('file', body.file);
    form.append('modo', body.modo);
    if (body.campaignId) form.append('campaignId', body.campaignId);
    if (body.badgeId) form.append('badgeId', body.badgeId);
    // Dry-run (asistente de carga, paso "Revisar"): valida todo sin escribir nada.
    if (body.preview) form.append('preview', 'true');
    return requestUpload<WinnerImportResultDto>(
      '/v1/winners/import', form, RECOGNITION_BASE_URL);
  },

  /**
   * GET v1/winners/import/template — descarga la plantilla como libro `.xlsx` (CPE-130 / Fase 3).
   *
   * `lang` (7 idiomas soportados) traduce los encabezados de la plantilla. Sin él, el backend
   * responde en español (default del servidor) — mismo patrón que `getLegalDocument`/
   * `getLandingBanner`, que ya mandan `lang` como query param.
   */
  getImportTemplate: (modo: 'vigente' | 'historica' = 'vigente', lang?: string) =>
    requestBlob(`/v1/winners/import/template${buildQuery({ modo, lang })}`),

  /** DELETE v1/winners/{id} — elimina un ganador. */
  remove: (id: string) =>
    request<{ success: boolean }>(`/v1/winners/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /**
   * Task 4.5 / spec §4.1 — GET v1/winners/nominations-export: el export CRUDO de nominaciones de
   * una campaña (`WinnersController.GetNominationsExport`, Task 4.4), paginado. El backend EXIGE
   * `campaignId` — no hay modo "todas las campañas" — y resuelve el alcance por OU/país de quien
   * pregunta; no se manda desde aquí, igual que en `search`.
   *
   * 🔴 `total` cuenta NOMINACIONES, no filas devueltas: el alcance filtra receptores fila por fila,
   * así que `filas.length` puede ser menor de lo que `total / pageSize` sugeriría. Quien pagina debe
   * cortar en página vacía (ver `exportarNominacionesCrudas` en `AdminWinners.tsx`), nunca contando
   * páginas por adelantado.
   */
  exportNominationsRaw: (campaignId: string, page: number, pageSize: number) =>
    request<NominationsRawExportPageDto>(
      `/v1/winners/nominations-export${buildQuery({ campaignId, page, pageSize })}`),
};

// ─── RecognitionService — Campañas (REQ-132) ────────────────────────────────────
export const campaignsApi = {
  /** GET v1/campaigns?status=&page=&pageSize= — listado paginado por estado. */
  getCampaigns: (status: CampaignStatus | undefined, page = 1, pageSize = 20) =>
    request<PagedResultDto<CampaignDto>>(`/v1/campaigns${buildQuery({ status, page, pageSize })}`),

  /** GET v1/campaigns/{id} — detalle de una campaña. */
  getById: (id: string) => request<CampaignDto>(`/v1/campaigns/${encodeURIComponent(id)}`),

  /** POST v1/campaigns — crea el borrador (wizard 5 pasos). */
  create: (body: CreateCampaignBody) =>
    request<CampaignDto>('/v1/campaigns', { method: 'POST', body: JSON.stringify(body) }),

  /** PATCH v1/campaigns/{id} — actualiza el borrador (puntos inmutables si ya arrancó). */
  update: (id: string, body: UpdateCampaignBody) =>
    request<CampaignDto>(`/v1/campaigns/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),

  /** POST v1/campaigns/{id}/documents — sube un PDF (multipart) a la campaña (FASE 3). */
  addDocument: (id: string, input: UploadCampaignDocumentInput) => {
    const form = new FormData();
    form.append('file', input.file);
    form.append('titulo', JSON.stringify(input.titulo));
    form.append('languageCode', input.languageCode);
    if (input.categoria) form.append('categoria', input.categoria);
    if (input.orden != null) form.append('orden', String(input.orden));
    return requestUpload<CampaignDocumentDto>(
      `/v1/campaigns/${encodeURIComponent(id)}/documents`, form, RECOGNITION_BASE_URL);
  },

  /** DELETE v1/campaigns/documents/{documentId} — elimina un documento. */
  deleteDocument: (documentId: string) =>
    request<{ success: boolean }>(`/v1/campaigns/documents/${encodeURIComponent(documentId)}`, { method: 'DELETE' }),

  /** POST v1/campaigns/{id}/approval — flujo de aprobación (submit/approve/reject). */
  approval: (id: string, action: CampaignApprovalAction, comentario?: string) =>
    request<CampaignApprovalDto>(`/v1/campaigns/${encodeURIComponent(id)}/approval`, {
      method: 'POST', body: JSON.stringify({ action, comentario: comentario ?? null }),
    }),

  /** POST v1/campaigns/{id}/panic — botón de pánico (finaliza inmediatamente). */
  panic: (id: string) =>
    request<CampaignDto>(`/v1/campaigns/${encodeURIComponent(id)}/panic`, { method: 'POST' }),

  /**
   * POST v1/campaigns/{id}/undo-panic — C2/P18-bis: deshace un pánico ACCIDENTAL.
   *
   * No es «reactivar cualquier campaña»: el backend exige que se cerrara POR PÁNICO, dentro de 24 h,
   * con la vigencia viva y sin que otra campaña haya ocupado su OU mientras tanto. Cualquiera de las
   * cuatro devuelve 422 con el motivo, y la pantalla lo enseña tal cual.
   */
  undoPanic: (id: string) =>
    request<CampaignDto>(`/v1/campaigns/${encodeURIComponent(id)}/undo-panic`, { method: 'POST' }),

  /** POST v1/campaigns/panic — pánico GLOBAL Brillamos (RF-073/REQ-029): finaliza TODAS las activas. */
  globalPanic: () =>
    request<{ finalizadas: number }>('/v1/campaigns/panic', { method: 'POST' }),

  /** POST v1/campaigns/{id}/duplicate?asChild= — duplica por OU (GAP-004). */
  duplicate: (id: string, newAlcance?: string[], asChild = false) =>
    request<CampaignDto>(
      `/v1/campaigns/${encodeURIComponent(id)}/duplicate${buildQuery({ asChild: String(asChild) })}`,
      { method: 'POST', body: JSON.stringify({ newAlcance: newAlcance ?? null }) },
    ),

  /** GET v1/campaigns/approvals/pending — aprobaciones pendientes (AdminGlobal). */
  getPendingApprovals: () => request<CampaignApprovalDto[]>('/v1/campaigns/approvals/pending'),

  /** GET v1/campaigns/approvals/mine — «Mis Solicitudes» (RN-C11): las del que pregunta, en todo estado. */
  getMyApprovals: () => request<CampaignApprovalDto[]>('/v1/campaigns/approvals/mine'),
};

// ─── RecognitionService — Campañas por Localidad / excepciones (CPE-138) ─────────
// Endpoints top-level v1/campaign-exceptions sobre RECOGNITION_BASE_URL. Un Admin OU las propone
// (nacen pendientes + inactivas); el Admin Global las aprueba/rechaza. Mismo `request`/`buildQuery`.
export const campaignExceptionsApi = {
  /** GET v1/campaign-exceptions — listado paginado con filtros (programa/baseCampaignId/activa/estado). */
  list: (params: CampaignExceptionListParams = {}) =>
    request<PagedResultDto<CampaignExceptionDto>>(
      `/v1/campaign-exceptions${buildQuery({
        programa: params.programa,
        baseCampaignId: params.baseCampaignId,
        activa: params.activa == null ? undefined : String(params.activa),
        estado: params.estado,
        page: params.page,
        pageSize: params.pageSize,
      })}`,
    ),

  /**
   * GET v1/campaign-exceptions/active-for-me — la excepción APROBADA + activa que aplica a QUIEN
   * PREGUNTA, resuelta por su cadena de OUs en el servidor. `undefined` (204) = ninguna aplica.
   *
   * Es la vía para un COLABORADOR. `list` de arriba es de administración y el backend la gatea con la
   * capacidad `CrearEditarCampanas`: pedirla desde una pantalla de usuario devolvía 403 a todo el que
   * no fuera admin. Además este endpoint filtra por estado `aprobada`, que el listado con
   * `activa=true` NO hacía — así una excepción reabierta a pendiente ya no puede colarse.
   */
  activeForMe: (programa: CampaignType) =>
    request<ActiveCampaignExceptionDto | undefined>(
      `/v1/campaign-exceptions/active-for-me${buildQuery({ programa })}`,
    ),

  /** GET v1/campaign-exceptions/{id} — detalle de una excepción. */
  getById: (id: string) =>
    request<CampaignExceptionDto>(`/v1/campaign-exceptions/${encodeURIComponent(id)}`),

  /** POST v1/campaign-exceptions — propone una excepción (nace pendiente + inactiva). */
  create: (body: CreateCampaignExceptionBody) =>
    request<CampaignExceptionDto>('/v1/campaign-exceptions', { method: 'POST', body: JSON.stringify(body) }),

  /** PUT v1/campaign-exceptions/{id} — edita una excepción (editar una aprobada la reabre a pendiente). */
  update: (id: string, body: UpdateCampaignExceptionBody) =>
    request<CampaignExceptionDto>(`/v1/campaign-exceptions/${encodeURIComponent(id)}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),

  /** DELETE v1/campaign-exceptions/{id} — baja lógica de una excepción. */
  remove: (id: string) =>
    request<{ success: boolean }>(`/v1/campaign-exceptions/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /** POST v1/campaign-exceptions/{id}/toggle — enciende/apaga (solo si está aprobada). */
  toggle: (id: string, activa: boolean) =>
    request<CampaignExceptionDto>(`/v1/campaign-exceptions/${encodeURIComponent(id)}/toggle`, {
      method: 'POST', body: JSON.stringify({ activa } satisfies ToggleCampaignExceptionBody),
    }),

  /** POST v1/campaign-exceptions/{id}/approval — flujo de aprobación (submit/approve/reject). */
  approval: (id: string, action: CampaignExceptionApprovalAction, comentario?: string) =>
    request<CampaignExceptionDto>(`/v1/campaign-exceptions/${encodeURIComponent(id)}/approval`, {
      method: 'POST', body: JSON.stringify({ action, comentario: comentario ?? null }),
    }),

  /** GET v1/campaign-exceptions/approvals/pending — bandeja de pendientes (Admin Global). */
  approvalsPending: () =>
    request<CampaignExceptionDto[]>('/v1/campaign-exceptions/approvals/pending'),

  // ── Tarea 15 — documentos de excepción (mismo patrón que campaignsApi.*Document*) ──

  /** GET v1/campaign-exceptions/{id}/documents — documentos PROPIOS de la excepción. */
  getDocuments: (exceptionId: string) =>
    request<CampaignExceptionDocumentDto[]>(
      `/v1/campaign-exceptions/${encodeURIComponent(exceptionId)}/documents`),

  /** POST v1/campaign-exceptions/{id}/documents — sube un PDF (multipart) a la excepción. */
  addDocument: (exceptionId: string, input: UploadCampaignExceptionDocumentInput) => {
    const form = new FormData();
    form.append('file', input.file);
    form.append('titulo', JSON.stringify(input.titulo));
    form.append('languageCode', input.languageCode);
    if (input.categoria) form.append('categoria', input.categoria);
    if (input.orden != null) form.append('orden', String(input.orden));
    return requestUpload<CampaignExceptionDocumentDto>(
      `/v1/campaign-exceptions/${encodeURIComponent(exceptionId)}/documents`, form, RECOGNITION_BASE_URL);
  },

  /** DELETE v1/campaign-exceptions/documents/{documentId} — elimina un documento (204 No Content). */
  deleteDocument: (documentId: string) =>
    request<void>(
      `/v1/campaign-exceptions/documents/${encodeURIComponent(documentId)}`, { method: 'DELETE' }),
};

// ─── RecognitionService — Gestión (REQ-134) ─────────────────────────────────────
export const gestionApi = {
  /** GET v1/admin/settings — documento de Gestión (GestionSettingsDto). */
  getSettings: () => request<GestionSettingsDto>('/v1/admin/settings'),

  /** WP-07.3 — GET v1/view-settings/banner?lang=: banner de Fundamentales (no-admin) para el hero del
   * landing. `lang` sirve solo ese idioma (evita mandar el logo data-URI de los 7 en cada carga). */
  getLandingBanner: (lang: string) =>
    request<LandingBannerDto>(`/v1/view-settings/banner${buildQuery({ lang })}`),

  /** GET v1/view-settings/recognition-rules?campaignId= — reglas de envío/perfil. `campaignId`
   *  opcional: cuando viene, el permiso de anónimo se resuelve para esa campaña. */
  getRecognitionRules: (campaignId?: string | null, programa?: string | null) =>
    request<RecognitionRulesDto>(
      `/v1/view-settings/recognition-rules${buildQuery({
        campaignId: campaignId ?? undefined,
        programa: programa ?? undefined,
      })}`,
    ),

  /** PUT v1/admin/settings — Guardar (reemplazo atómico). */
  saveSettings: (body: GestionSettingsDto) =>
    request<GestionSettingsDto>('/v1/admin/settings', { method: 'PUT', body: JSON.stringify(body) }),

  /** GET v1/admin/program-settings/{programa} — reglas por programa (campos anulables; null=hereda global). */
  getProgramSettings: (programa: string) =>
    request<RecognitionProgramSettingsDto>(`/v1/admin/program-settings/${encodeURIComponent(programa)}`),

  /** PUT v1/admin/program-settings/{programa} — Guardar reglas del programa (escritura parcial; null=hereda). */
  saveProgramSettings: (programa: string, body: RecognitionProgramSettingsDto) =>
    request<RecognitionProgramSettingsDto>(
      `/v1/admin/program-settings/${encodeURIComponent(programa)}`,
      { method: 'PUT', body: JSON.stringify(body) }),

  /** GET v1/admin/settings/badges — catálogo de insignias. */
  getBadges: () => request<AdminBadgeDto[]>('/v1/admin/settings/badges'),

  /** PUT v1/admin/settings/badges — edita una insignia (nombre ×7, color, semanas). */
  saveBadge: (badge: AdminBadgeDto) =>
    request<AdminBadgeDto>('/v1/admin/settings/badges', { method: 'PUT', body: JSON.stringify(badge) }),

  /** PUT v1/admin/settings/streak-freeze — mínimo semanal, freeze global y apagado de racha. */
  saveStreakSettings: (body: StreakSettingsBody) =>
    request<{ success?: boolean }>('/v1/admin/settings/streak-freeze', { method: 'PUT', body: JSON.stringify(body) }),

  /** POST v1/admin/settings/badges/{id}/image — sube/reemplaza el ícono de una insignia ya guardada. */
  uploadBadgeImage: (badgeId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return requestUpload<AdminBadgeDto>(
      `/v1/admin/settings/badges/${encodeURIComponent(badgeId)}/image`, form, RECOGNITION_BASE_URL);
  },
};

// ─── RecognitionService — Recap anual (Wrapped) ─────────────────────────────────
export const recapApi = {
  /** GET v1/admin/recap — historial de ediciones (+ ganadores anuales reales). */
  getEditions: () => request<RecapEditionSummaryDto[]>('/v1/admin/recap'),

  /** GET v1/admin/recap/{anio} — configuración completa de la edición (404 si no existe). */
  getEdition: (anio: number) => request<RecapEditionDto>(`/v1/admin/recap/${anio}`),

  /** POST v1/admin/recap — crea una edición sembrada con los slides del producto. */
  createEdition: (anio: number, nombre?: string) =>
    request<RecapEditionDto>('/v1/admin/recap', {
      method: 'POST', body: JSON.stringify({ anio, nombre: nombre ?? null }),
    }),

  /** PUT v1/admin/recap/{anio} — Guardar (reemplazo atómico). */
  saveEdition: (anio: number, body: RecapEditionDto) =>
    request<RecapEditionDto>(`/v1/admin/recap/${anio}`, { method: 'PUT', body: JSON.stringify(body) }),

  /** POST v1/admin/recap/{anio}/notify — difunde "tu Recap ya está listo" al alcance. */
  notify: (anio: number) =>
    request<RecapEditionDto>(`/v1/admin/recap/${anio}/notify`, { method: 'POST' }),

  /**
   * GET v1/recap/current?ouId= — edición VIGENTE para el usuario final (no admin): activa, dentro de
   * la ventana de visibilidad y con su OU en el alcance. 204 ⇒ `undefined` (no hay ninguna vigente).
   *
   * RF-130 — Es la configuración que gobierna el overlay: fondo, icono, título ×7 y etiquetas de cada
   * slide. El endpoint existía en el backend desde hace meses SIN un solo consumidor, de modo que la
   * pantalla Admin › Recap no afectaba a nada de lo que veía el colaborador.
   *
   * Los slides llegan YA filtrados por `activo` y ordenados por `indice`
   * (`GetCurrentRecapQueryHandler.cs:21-24`): el cliente NO los vuelve a filtrar ni a ordenar, o la
   * regla de negocio quedaría duplicada en dos sitios.
   */
  getCurrent: (ouId?: string, anio?: number) =>
    request<RecapEditionDto | undefined>(`/v1/recap/current${buildQuery({ ouId, anio })}`),

  /**
   * GET v1/recap/availability — RF-130: ¿puede el TITULAR ver su Wrapped, y de qué año?
   *
   * Sin parámetros a propósito: el titular y su OU salen del token, nunca del query string (declarar
   * la OU de una geografía que ya cerró su campaña adelantaría el Wrapped). Devuelve SIEMPRE 200: "no
   * disponible" es una respuesta legítima, no un error.
   */
  getAvailability: () => request<WrappedAvailabilityDto>('/v1/recap/availability'),
};

// ─── Utilidades de error ─────────────────────────────────────────────────────

/**
 * Extrae el mensaje para el usuario de un error de la API de Reconocimiento. El backend responde
 * RFC 7807 (`ErrorDto`): `title` es el mensaje de negocio (auto-reco, tope semanal, palabra bloqueada,
 * límite de fundamentales → 422; sin aceptación legal → 403). Cae a `fallback` si no es un error tipado
 * o si nunca hubo respuesta HTTP (config ausente, `status === 0`, o `NETWORK_ERROR_STATUS`: el cliente
 * se rindió antes de que el servidor respondiera). Para ESE último caso, una pantalla que sepa que su
 * operación puede seguir corriendo en el servidor (p. ej. una carga masiva grande) debe consultar
 * `getRecognitionErrorStatus`/`getRecognitionErrorCorrelationId` aparte y armar su propio mensaje
 * específico en vez de mostrar este `fallback` genérico — ver `StepConfirm.tsx` de la carga Europa/EE. UU.
 */
export function getRecognitionErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof RecognitionApiError) {
    if (error.status === 0 || error.status === NETWORK_ERROR_STATUS) return fallback;
    const body = error.body as RecognitionErrorBody | { message?: string } | null;
    if (body && typeof body === 'object') {
      if ('title' in body && typeof body.title === 'string' && body.title) return body.title;
      if ('message' in body && typeof body.message === 'string' && body.message) return body.message;
    }
  }
  return fallback;
}

/**
 * Correlation id del request que falló, o `undefined` si no es un `RecognitionApiError` o el error
 * ocurrió antes de generarlo (config ausente, `status === 0`). Con `NETWORK_ERROR_STATUS` SIEMPRE
 * viene presente: es la referencia que permite, después, cruzar el incidente contra el registro
 * persistido del backend (p. ej. `ImportAudit.CorrelationId`) aunque los logs efímeros ya no existan.
 */
export function getRecognitionErrorCorrelationId(error: unknown): string | undefined {
  return error instanceof RecognitionApiError ? error.correlationId : undefined;
}

/**
 * Código de motivo que el backend adjunta en `errors.reason` (RF-090: el 422 del ✉️).
 *
 * Se rinde el CÓDIGO y no el `title` porque el título del problem-details viene solo en español y
 * esta pantalla habla 7 idiomas. `undefined` si la respuesta no trae motivo.
 */
export function getRecognitionErrorReason(error: unknown): string | undefined {
  if (!(error instanceof RecognitionApiError)) return undefined;
  const body = error.body as RecognitionErrorBody | null;
  const reason = body?.errors?.reason?.[0];
  return typeof reason === 'string' && reason ? reason : undefined;
}

/** HTTP status del error, o `undefined` si no es un `RecognitionApiError`. */
export function getRecognitionErrorStatus(error: unknown): number | undefined {
  return error instanceof RecognitionApiError ? error.status : undefined;
}

/**
 * Código estable + parámetros que el backend adjunta en `errors` para que un rechazo se traduzca a los
 * 7 idiomas en vez de mostrar el `title` en español (p. ej. el alta de campaña:
 * `errors.code = ["campaign.endBeforeStart"]`). El resto de claves de `errors` (menos `code`) viajan
 * como parámetros de interpolación. `undefined` si no trae `code`: el consumidor cae al texto del
 * backend con `getRecognitionErrorMessage` (comportamiento previo).
 *
 * Lo emiten dos caminos del ExceptionMiddleware: las reglas de negocio de dominio (422) y las
 * validaciones de FluentValidation (400, bug UAT #113).
 */
export function getRecognitionErrorCode(
  error: unknown,
): { code: string; params: Record<string, string> } | undefined {
  if (!(error instanceof RecognitionApiError)) return undefined;
  const errors = (error.body as RecognitionErrorBody | null)?.errors;
  const code = errors?.code?.[0];
  if (typeof code !== 'string' || !code) return undefined;
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(errors ?? {})) {
    if (key !== 'code' && Array.isArray(value) && typeof value[0] === 'string') {
      params[key] = value[0];
    }
  }
  return { code, params };
}

/** Mapea un `UserDto` del UserAdminService a nuestra `PersonSummary` (subconjunto estable). */
export function toPersonSummary(u: {
  id: string;
  email: string;
  displayName?: string | null;
  jobTitle?: string | null;
  ouId?: string | null;
  ouName?: string | null;
  photoUrl?: string | null;
  avatar?: string | null;
}): PersonSummary {
  return {
    id: u.id,
    email: u.email,
    // P3-4 — nombre natural, no el `displayName` crudo del AD. Misma regla que la identidad
    // propia (`useCurrentUser`), para que el buscador de compañeros, el muro y el Wrapped no
    // mezclen "Ernesto de la Cruz" con "(Ext.) De la Cruz Morales, Ernesto".
    displayName: u.displayName ? toNaturalName(u.displayName) : null,
    jobTitle: u.jobTitle ?? null,
    ouId: u.ouId ?? null,
    ouName: u.ouName ?? null,
    photoUrl: u.photoUrl ?? null,
    // RF-008 — este mapeo va campo por campo, así que un campo nuevo del `UserDto` se PIERDE en
    // silencio si no se añade también aquí. Pasa por aquí `getUserById`, que es de donde sale la
    // tarjeta de perfil de un colega.
    avatar: u.avatar ?? null,
  };
}
